// ===== 必要なReactフックや型、アイコン、コンポーネントのインポート =====
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useWindowSize } from "./hooks/useWindowSize.ts";
import { useResizablePanelWidth } from "./hooks/useResizablePanelWidth.ts";
import { PanelResizeHandle } from "./components/PanelResizeHandle.tsx";
import {
  useFilterStorage,
  loadSimpleFilters,
} from "./hooks/useFilterStorage.ts";
import {
  useTodos,
  TaskUpdateDto,
  TodoListCreateDto,
  TaskCreateDto,
  SubTaskCreateDto,
  TodoList,
} from "./hooks/useTodos.ts";
import type {
  Task,
  User,
  Tag,
  FilterState,
  MyFilter,
  SortKey,
  SortDirection,
  RoomStatus,
  EditingChildTask,
  CustomColumnValue,
} from "./types/types";
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  ListAlt as ListAltIcon,
  SwapVert as SwapVertIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarMonthIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";

import { TaskItem } from "./components/TaskItem.tsx";
import { TaskDialog } from "./components/TaskDialog.tsx";
import { FilterDialog } from "./components/FilterDialog.tsx";
import { DeadlineCountBadges } from "./components/DeadlineCountBadges.tsx";
import { todayYmd, countDeadlineStates } from "./utils/date.ts";
import { TagManagementDialog } from "./components/TagManagementDialog.tsx";
import { TaskBoardView } from "./components/TaskBoardView.tsx";
import { TimelineView } from "./components/TimelineView.tsx";
import { TaskDetailPanel } from "./components/TaskDetailPanel.tsx";
import { ListCreateDialog } from "./components/ListCreateDialog.tsx";
import { ListEditDialog } from "./components/ListEditDialog.tsx";
import { LoadingOverlay } from "./components/loadingOverlay.tsx";
import { TaskTableView } from "./components/TaskTableView.tsx";
import { TaskMapView } from "./components/TaskMapView.tsx";
import { GeneralSettingsDialog } from "./components/GeneralSettingsDialog.tsx";
import { ReminderManagementDialog } from "./components/ReminderManagementDialog.tsx";
import { TaskExportDialog } from "./components/TaskExportDialog.tsx";
import { RecurrenceManagementDialog } from "./components/RecurrenceManagementDialog.tsx";
import { ConfirmDialog } from "./components/ConfirmDialog.tsx";
import { ProjectMemoPanel } from "./components/ProjectMemoPanel.tsx";
import { SaveProjectTemplateModal } from "./components/SaveProjectTemplateModal.tsx";
import { ProjectTemplateEditorPanel } from "./components/ProjectTemplateEditorPanel.tsx";
import { getMyPaletteColor } from "./utils/colorUtils.ts";
import { loadStoredAuth, saveStoredAuth } from "./utils/authStorage.ts";
import {
  clearReentering,
  installApiAuthInterceptor,
  reenterViaChatis,
  restoreApiAuth,
  setApiAuth,
  setAuthFailureHandler,
} from "./utils/ApiAuth.ts";
import { uploadTaskFile } from "./utils/fileApi.ts";
import { PaletteColor, PaletteColorHex } from "./types/PaletteColor.ts";
// import {
//   DragDropContext,
//   Droppable,
//   Draggable,
//   DropResult,
// } from "react-beautiful-dnd";
import "./custom.css";
import PlaylistAddCheckCircleTwoToneIcon from "@mui/icons-material/PlaylistAddCheckCircleTwoTone";
import LibraryAddTwoToneIcon from "@mui/icons-material/LibraryAddTwoTone";
import ExtensionTwoToneIcon from "@mui/icons-material/ExtensionTwoTone";
import NightsStayTwoToneIcon from "@mui/icons-material/NightsStayTwoTone";
import { TaskItemCustomColumnDialog } from "./components/TaskItemCustomColumnDialog.tsx";
import {
  loadVisibleCustomColumnIds,
  saveVisibleCustomColumnIds,
} from "./utils/customColumnDisplay.ts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AnnouncementTwoToneIcon from "@mui/icons-material/AnnouncementTwoTone";
import DriveFileRenameOutlineTwoToneIcon from "@mui/icons-material/DriveFileRenameOutlineTwoTone";
import FilterAltTwoToneIcon from "@mui/icons-material/FilterAltTwoTone";
import ViewHeadlineTwoToneIcon from "@mui/icons-material/ViewHeadlineTwoTone";
import ViewDayTwoToneIcon from "@mui/icons-material/ViewDayTwoTone";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import KebabDiningOutlinedIcon from "@mui/icons-material/KebabDiningOutlined";
import SortIcon from "@mui/icons-material/Sort";
import AlarmTwoToneIcon from "@mui/icons-material/AlarmTwoTone";
import FileDownloadTwoToneIcon from "@mui/icons-material/FileDownloadTwoTone";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ManageHistoryTwoToneIcon from "@mui/icons-material/ManageHistoryTwoTone";
import ChatIcon from "@mui/icons-material/Chat";
import { RecurrenceUpdatePrompt } from "./components/RecurrenceUpdatePrompt.tsx";
import { HelpDrawer } from "./components/HelpDrawer.tsx";
import { ShortcutHeader } from "./components/ShortcutHeader.tsx";
import { DashboardView } from "./components/DashboardView.tsx";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import TravelExploreIcon from "@mui/icons-material/TravelExplore";

function buildChildTasksForContents(childTasks: Task[]): object[] {
  return childTasks.map((child) => ({
    id: child.id, // サブタスクのカスタム項目値を DB から補完するためのひも付け用
    title: child.title,
    memo: child.memo || "",
    priority: child.priority,
    notification: child.notification,
    assignees: (child.assignees || []).map((a) => ({
      id: parseInt(a.id),
      name: a.name,
    })),
    requesters: (child.requesters || []).map((r) => ({
      id: parseInt(r.id),
      name: r.name,
    })),
    tags: (child.tags || []).map((t) => ({
      id: t.id,
      name: t.name,
      color: t.color,
    })),
    childTasks: buildChildTasksForContents(child.childTasks || []),
  }));
}

// ===== フィルターの初期値定義 =====
const initialFilterState: FilterState = {
  statuses: [],
  startDate: "",
  endDate: "",
  assigneeIds: [],
  assigneeOperator: "OR",
  requesterIds: [],
  requesterOperator: "OR",
  tagIds: [],
  tagOperator: "OR",
  starredOnly: false,
  myTasksOnly: false,
  myRequestsOnly: false,
  dueTodayOnly: false,
  overdueOnly: false,
  priorities: [],
  showCompleted: false,
  showHidden: false,
  searchKeyword: "",
  searchMode: "name",
  customColumnConditions: [],
};

// fetch インターセプタの設置。"/api/" 宛のリクエストにアクセストークンを自動付与する。
// 最初の API 呼び出しより先に差し替える必要があるため、モジュール読み込み時に実行する。
installApiAuthInterceptor();

// ===== URLパラメータからhonbuId/roomId/color/darkModeを取得するカスタムフック =====
// 認証はサーバー側の API アクセストークンで行うため、ここでは認証判定を行わない。
// localStorage は表示設定（color / isDarkMode）の復元にのみ使用する。
function useUrlParams() {
  const [params, setParams] = useState<{
    honbuId: number | null;
    roomId: number | null;
    color: PaletteColor;
    isDarkMode: boolean;
    loaded: boolean;
    isShortcut: boolean;
  }>({
    honbuId: null,
    roomId: null,
    color: "Blue",
    isDarkMode: false,
    loaded: false,
    isShortcut: false,
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const honbuIdParam = queryParams.get("honbuid");
    const roomIdParam = queryParams.get("roomid");

    let honbuId: number | null = null;
    let roomId: number | null = null;
    let color: PaletteColor = "Blue";
    let isDarkMode = false;
    let isShortcut = false;

    if (honbuIdParam && roomIdParam) {
      honbuId = parseInt(honbuIdParam, 10);
      roomId = parseInt(roomIdParam, 10);
      isShortcut = queryParams.get("shortcut") === "1";

      if (isShortcut) {
        // ショートカット URL には color / mode が付かないため前回の表示設定を復元する
        const stored = loadStoredAuth(honbuId, roomId);
        if (stored) {
          color = stored.color;
          isDarkMode = stored.isDarkMode;
        }
      }
      // URL に指定があればそちらを優先する（通常アクセス・/goto 経由の再入場）
      if (queryParams.has("color")) {
        color = getMyPaletteColor(queryParams.get("color"));
      }
      if (queryParams.has("mode")) {
        isDarkMode = queryParams.get("mode") === "dark";
      }
    }
    // URLパラメータなしの場合: honbuId=null, roomId=null → NoAuthScreen を表示

    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    document.documentElement.style.setProperty(
      "--palette-color",
      PaletteColorHex[color]
    );

    setParams({
      honbuId,
      roomId,
      color,
      isDarkMode,
      loaded: true,
      isShortcut,
    });
  }, []);

  return params;
}

// ===== PC用のメインビューコンポーネント =====
function DesktopView({
  honbuId,
  roomId,
  setAppLoading,
}: {
  honbuId: number;
  roomId: number;
  setAppLoading: (isLoading: boolean) => void;
}) {
  // 画面幅取得（サイドパネル表示切替用）
  const { width } = useWindowSize();

  // URLパラメータ取得からcolorを取得
  const { color, isDarkMode } = useUrlParams();

  // ヘルプドロワーの開閉状態
  // モバイル閾値(768px) + ヘルプ幅(420px) = 1188px 以上でパネルモード
  const [helpOpen, setHelpOpen] = useState(false);
  const helpVariant: "overlay" | "panel" = width >= 1188 ? "panel" : "overlay";
  // ヘルプパネルがパネルモードで開いている場合の幅（overlayや root の padding に使用）
  const helpPanelWidth = helpOpen && helpVariant === "panel" ? 420 : 0;

  // ヘルプパネルがパネルモードで開いている場合、420px分を差し引いた幅で判定
  // （ヘルプを開くと詳細パネルが非表示になる場合がある — これは意図した動作）
  const effectiveWidthForDetail =
    helpOpen && helpVariant === "panel" ? width - 420 : width;
  const isWideScreen = effectiveWidthForDetail > 1300;

  // ? キーでヘルプを開く / postMessage でヘルプを開く
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "?" &&
        !["INPUT", "TEXTAREA"].includes((e.target as HTMLElement).tagName)
      ) {
        setHelpOpen((prev) => !prev);
      }
    };
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type === "openHelp") setHelpOpen(true);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // サイドバー開閉状態
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  //サイドバー内コンテンツの開閉状態
  const [isProjectListOpen, setIsProjectListOpen] = useState(true);
  const [isSortOpen, setIsSortOpen] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isMyFilterOpen, setIsMyFilterOpen] = useState(true);
  const [isOtherOpen, setIsOtherOpen] = useState(true);

  // リマインダー管理・自動作成管理ダイアログの表示状態
  const [showReminderManagement, setShowReminderManagement] = useState(false);
  // タスクデータ出力ダイアログの表示状態
  const [showTaskExport, setShowTaskExport] = useState(false);
  const [showRecurrenceManagement, setShowRecurrenceManagement] =
    useState(false);
  const [recurrenceInitialTaskId, setRecurrenceInitialTaskId] = useState<
    string | undefined
  >(undefined);
  const [recurrenceInitialRuleId, setRecurrenceInitialRuleId] = useState<
    number | undefined
  >(undefined);

  // 自動作成設定の同期プロンプト状態
  const [recurrenceUpdatePrompt, setRecurrenceUpdatePrompt] = useState<{
    ruleId: number;
    task: Task;
    isOriginal: boolean;
    customColumnValues?: CustomColumnValue[];
  } | null>(null);
  const [recurrenceUpdateLoading, setRecurrenceUpdateLoading] = useState(false);

  // リマインダー管理・自動作成管理用の全タスク（完了・非表示含む）
  const [allTasksForDialogs, setAllTasksForDialogs] = useState<Task[]>([]);
  // サイドバーのスクロール状態
  const [hasScrolled, setHasScrolled] = useState(false);
  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const st = e.currentTarget.scrollTop;
    if (!hasScrolled && st > 4) setHasScrolled(true);
    else if (hasScrolled && st <= 4) setHasScrolled(false);
  };

  // 各種フィルター・ソート状態
  const [filters, setFilters] = useState<FilterState>(() => {
    // 初期化時にLocalStorageから読み込む
    const savedFilters = loadSimpleFilters(roomId);
    return {
      ...initialFilterState,
      ...savedFilters,
    };
  });
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // roomId変更を検知するためのref（初期値は-1で初回検知を確実にする）
  const prevRoomIdRef = useRef<number>(-1);

  // プロジェクト（roomId）変更時にフィルターをLocalStorageから復元
  useEffect(() => {
    // roomIdが実際に変更された場合のみ実行
    if (roomId && roomId !== prevRoomIdRef.current) {
      const prevRoomId = prevRoomIdRef.current;
      prevRoomIdRef.current = roomId;

      // 初回（-1から変更）でない場合のみ復元処理を実行
      if (prevRoomId !== -1) {
        const savedFilters = loadSimpleFilters(roomId);
        setFilters((prev) => ({
          ...prev,
          myTasksOnly: savedFilters.myTasksOnly ?? false,
          myRequestsOnly: savedFilters.myRequestsOnly ?? false,
          dueTodayOnly: savedFilters.dueTodayOnly ?? false,
          overdueOnly: savedFilters.overdueOnly ?? false,
          starredOnly: savedFilters.starredOnly ?? false,
          showCompleted: savedFilters.showCompleted ?? false,
          showHidden: savedFilters.showHidden ?? false,
        }));
      }
    }
  }, [roomId]);

  // フィルター状態の自動保存
  useFilterStorage(roomId, filters);

  // ルームステータス取得

  const [roomStatuses, setRoomStatuses] = useState<RoomStatus[]>([]);
  useEffect(() => {
    if (roomId === null) return;
    const fetchStatuses = async () => {
      try {
        const response = await fetch(
          `/api/rooms/${roomId}/statuses?honbuId=${honbuId}`,
        );
        if (response.ok) {
          setRoomStatuses(await response.json());
        }
      } catch (error) {
        console.error("Failed to fetch room statuses:", error);
      }
    };
    fetchStatuses();
  }, [roomId]);

  // タスク・リスト・操作関数群（useTodosカスタムフック）
  const {
    todoLists,
    setTodoLists,
    activeListId,
    setActiveListId,
    filteredTasks,
    toggleTaskComplete,
    updateTaskStatus,
    toggleTaskStar,
    toggleTaskNotification,
    addTodoList,
    addTask,
    addBulkTasks,
    refreshCustomColumnValues,
    updateTask,
    fetchAndMergeTasks,
    updateListStatus,
    deleteList,
    deleteTask,
    addReminder,
    deleteReminder,
    refreshTodos,
    fetchAllLists,
    isLoading,
    updateListName,
    addSubtask,
    reorderSubtasks,
    forceReload,
    updateRecurrenceContents,
    customColumns,
    customColumnValues,
    refreshCustomColumns,
  } = useTodos(roomId, honbuId, filters, roomStatuses);

  // 全体設定でカスタム列が変更された際に DashboardView へ再取得を通知するカウンター
  const [customColumnsVersion, setCustomColumnsVersion] = useState(0);

  // TaskList表示用のカスタム列設定（プロジェクトごと・localStorage）
  const [visibleCustomColumnIds, setVisibleCustomColumnIds] = useState<
    number[]
  >([]);
  const [showCustomColumnDialog, setShowCustomColumnDialog] = useState(false);

  // アクティブリストが変わったら表示設定を読み込む
  useEffect(() => {
    setVisibleCustomColumnIds(
      loadVisibleCustomColumnIds(honbuId, roomId, activeListId),
    );
  }, [honbuId, roomId, activeListId]);

  const handleSaveVisibleCustomColumns = useCallback(
    (ids: number[]) => {
      saveVisibleCustomColumnIds(honbuId, roomId, activeListId, ids);
      setVisibleCustomColumnIds(ids);
    },
    [honbuId, roomId, activeListId],
  );

  // 詳細パネルで選択中のタスク
  const [selectedDetail, setSelectedDetail] = useState<{
    parent: Task;
    child?: Task;
  } | null>(null);
  // 新規作成されたタスクIDを保持（選択用）
  const pendingSelectTaskIdRef = useRef<{
    taskId: string;
    view: "list" | "table" | "timeline" | "board" | "taskmap" | "dashboard";
  } | null>(null);
  // 新規作成されたサブタスクIDを保持（詳細パネル遷移用）
  const pendingSelectSubtaskRef = useRef<{
    parentTaskId: string;
    subtaskId: string;
  } | null>(null);
  // コピーで作成されたタスクIDを保持（コピー後に詳細パネルへ選択・表示するため）
  const pendingSelectCopiedTaskIdRef = useRef<{
    taskId: string;
    view: "list" | "table" | "timeline" | "board" | "taskmap" | "dashboard";
  } | null>(null);
  // リストビューのスクロールコンテナへの参照
  const listScrollContainerRef = useRef<HTMLDivElement | null>(null);
  // テーブルビューのスクロールコンテナへの参照
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  // タスクマップビューのスクロールコンテナへの参照
  const taskMapScrollContainerRef = useRef<HTMLDivElement | null>(null);
  // 編集中のプロジェクトテンプレートIDを管理するステート
  const [editingProjectTemplateId, setEditingProjectTemplateId] = useState<
    number | null
  >(null);
  // プロジェクトメニューの開閉状態を管理するステート
  const [openMenuListId, setOpenMenuListId] = useState<string | null>(null);

  // PC幅狭モード用のオーバーレイ表示
  const [isDetailOverlayOpen, setIsDetailOverlayOpen] = useState(false);

  // 詳細パネルの幅。静的パネル・オーバーレイ・詳細モーダルで共通。
  // 左辺のハンドルをドラッグして一時的に変更できる（state のみ管理・リロードで既定に戻る）。
  const { width: detailPanelWidth, handleResizeStart: handleDetailResizeStart } =
    useResizablePanelWidth();
  // プロジェクトメモオーバーレイ（PC幅狭・全ビュー共通）
  const [isMemoOverlayOpen, setIsMemoOverlayOpen] = useState(false);
  // プロジェクトメモ未読バッジ
  const [hasMemoUnread, setHasMemoUnread] = useState(false);
  // タイムラインやボードビューから詳細表示
  const [modalTask, setModalTask] = useState<Task | null>(null);
  // ビュー切替（リスト/テーブル/タイムライン/ボード/ダッシュボード）
  const [viewMode, setViewMode] = useState<
    "list" | "table" | "timeline" | "board" | "taskmap" | "dashboard"
  >(() => {
    return "list"; // 一旦デフォルト値
  });
  // 詳細フィルターダイアログ表示
  const [showDetailedFilter, setShowDetailedFilter] = useState(false);
  // タスク作成・編集ダイアログ表示
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  // タグ管理ダイアログ表示
  const [showTagManagement, setShowTagManagement] = useState(false);
  // 新規リスト作成ダイアログ表示
  const [showCreatePrompt, setShowCreatePrompt] = useState(false);
  // ダイアログ表示状態
  const [showCreateListDialog, setShowCreateListDialog] = useState(false);
  const [showEditListDialog, setShowEditListDialog] = useState(false);
  const [showGeneralSettingsDialog, setShowGeneralSettingsDialog] =
    useState(false);

  const findTaskById = useCallback(
    (sTasks: Task[], id: string): Task | null => {
      for (const t of sTasks) {
        if (t.id === id) return t;
        if (t.childTasks && t.childTasks.length > 0) {
          const found = findTaskById(t.childTasks, id);
          if (found) return found;
        }
      }
      return null;
    },
    [],
  );

  // 編集ダイアログを開く
  const handleOpenEditListDialog = async () => {
    await fetchAllLists(); // 全リストを再取得
    setShowEditListDialog(true);
  };

  // ダイアログを閉じる（設定変更があった場合はリストを再取得）
  const handleCloseDialogs = (settingsChanged: boolean) => {
    setShowCreateListDialog(false);
    setShowEditListDialog(false);
    setShowCreatePrompt(false);
    if (settingsChanged) {
      refreshTodos();
    }
  };

  // const handleUpdateListName = async (listId: string, name: string) => {
  //   try {
  //     const response = await fetch(`/api/todolists/${listId}/name`, {
  //       method: "PUT",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({ name }),
  //     });
  //     if (!response.ok) throw new Error("Failed to update list name");

  //     // 成功したらリストを再取得して画面を更新
  //     refreshTodos();
  //   } catch (error) {
  //     console.error(error);
  //     // エラー時の処理
  //   }
  // };

  // roomIdが0以外に切り替わったらマイリスト選択解除
  useEffect(() => {
    if (roomId !== 0 && activeListId === "0") {
      const first = todoLists.find((l) => l.status === "Active");
      if (first) setActiveListId(first.id);
    }
  }, [roomId, activeListId, todoLists, setActiveListId]);

  // Myフィルター関連
  const [myFilters, setMyFilters] = useState<MyFilter[]>([]);
  const [activeMyFilterId, setActiveMyFilterId] = useState<number | null>(null);
  const [editingMyFilter, setEditingMyFilter] = useState<MyFilter | null>(null);

  // タグ・メンバー取得用
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [roomMembers, setRoomMembers] = useState<User[]>([]);
  const [initialStatusForNewTask, setInitialStatusForNewTask] = useState<
    Task["status"] | undefined
  >();

  // 各種ローディング状態
  const [areFiltersLoading, setAreFiltersLoading] = useState(true);
  const [areTagsLoading, setAreTagsLoading] = useState(true);
  const [areMembersLoading, setAreMembersLoading] = useState(true);

  // プロジェクトピン関連のstate
  const [projectPins, setProjectPins] = useState<Map<number, Date>>(new Map());
  const [arePinsLoading, setArePinsLoading] = useState(true);

  // プロジェクトピンを取得
  useEffect(() => {
    if (honbuId === null) return;
    setArePinsLoading(true);
    (async () => {
      try {
        const response = await fetch(`/api/project-pins?honbuId=${honbuId}`);
        if (response.ok) {
          const pins = await response.json();
          const pinMap = new Map<number, Date>();
          pins.forEach((pin: any) => {
            pinMap.set(pin.todoListId, new Date(pin.pinnedAt));
          });
          setProjectPins(pinMap);
        }
      } catch (error) {
        console.error("Failed to fetch project pins:", error);
      } finally {
        setArePinsLoading(false);
      }
    })();
  }, [honbuId]);

  // ピン留めのトグル
  const toggleProjectPin = async (listId: string) => {
    const todoListId = parseInt(listId, 10);
    const isPinned = projectPins.has(todoListId);

    // 楽観的更新
    setProjectPins((prev) => {
      const newMap = new Map(prev);
      if (isPinned) {
        newMap.delete(todoListId);
      } else {
        newMap.set(todoListId, new Date());
      }
      return newMap;
    });

    try {
      if (isPinned) {
        const response = await fetch(
          `/api/project-pins/${todoListId}?honbuId=${honbuId}`,
          { method: "DELETE" },
        );
        if (!response.ok) throw new Error("Failed to unpin");
      } else {
        const response = await fetch("/api/project-pins", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ honbuId, todoListId }),
        });
        if (!response.ok) throw new Error("Failed to pin");
      }
    } catch (error) {
      console.error("Failed to toggle pin:", error);
      // ロールバック
      setProjectPins((prev) => {
        const newMap = new Map(prev);
        if (isPinned) {
          newMap.set(todoListId, new Date());
        } else {
          newMap.delete(todoListId);
        }
        return newMap;
      });
    }
  };

  // 表示するリスト（ピン留めでソート）
  const visibleLists = useMemo(() => {
    const lists =
      roomId === 0 ? todoLists : todoLists.filter((l) => l.status === "Active");

    // ピン留めされているものを上に、新しいピン留め順（pinnedAtが新しい順）でソート
    return [...lists].sort((a, b) => {
      const aPinned = projectPins.has(parseInt(a.id, 10));
      const bPinned = projectPins.has(parseInt(b.id, 10));

      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;

      if (aPinned && bPinned) {
        const aDate = projectPins.get(parseInt(a.id, 10))!;
        const bDate = projectPins.get(parseInt(b.id, 10))!;
        return bDate.getTime() - aDate.getTime(); // 新しい順
      }

      return 0; // 両方ピンなしの場合は元の順序を維持
    });
  }, [todoLists, roomId, projectPins]);

  // プロジェクト一覧のバッジ用: リストごとの期限切れ・今日期限の件数（サブタスク含む）
  const deadlineCountsByList = useMemo(() => {
    const today = todayYmd();
    const map = new Map<string, { overdue: number; today: number }>();
    for (const list of todoLists) {
      map.set(list.id, countDeadlineStates(list.tasks ?? [], today));
    }
    return map;
  }, [todoLists]);

  // ピンとリストの読み込みが完了したら、一番上のプロジェクトを選択
  useEffect(() => {
    // ピンとリストの読み込みが完了していない場合はスキップ
    if (arePinsLoading || isLoading) return;

    // visibleListsが空の場合はスキップ
    if (visibleLists.length === 0) return;

    // 既にactiveListIdが有効な値で設定されている場合はスキップ
    // ただし、初回ロード時（activeListIdが未設定または"0"）は選択を行う
    if (activeListId && activeListId !== "0") {
      // activeListIdがvisibleListsに存在するか確認
      const exists = visibleLists.some((l) => l.id === activeListId);
      if (exists) {
        return; // 既に有効なプロジェクトが選択されている
      }
    }

    // visibleListsの一番上を選択（ピン留めでソート済みなので、最新のピン留めが一番上）
    setActiveListId(visibleLists[0].id);
  }, [arePinsLoading, isLoading, visibleLists, activeListId, setActiveListId]);

  // 全ローディング完了時にAppローディング解除（ピンも追加）
  useEffect(() => {
    if (
      !isLoading &&
      !areFiltersLoading &&
      !areTagsLoading &&
      !areMembersLoading &&
      !arePinsLoading
    ) {
      setAppLoading(false);
    }
  }, [
    isLoading,
    areFiltersLoading,
    areTagsLoading,
    areMembersLoading,
    arePinsLoading,
    setAppLoading,
  ]);

  // プロジェクトメモ未読チェック（アクティブリスト変更時）
  useEffect(() => {
    if (!activeListId || activeListId === "0") {
      setHasMemoUnread(false);
      return;
    }
    fetch(
      `/api/todolists/${activeListId}/memos/has-unread?honbuId=${honbuId}&roomId=${roomId}`,
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setHasMemoUnread(data.hasUnread);
      })
      .catch(console.error);
  }, [activeListId, honbuId, roomId]);

  // タイムライン用ユーザーリスト
  const timelineUsers = useMemo(() => {
    if (filters.assigneeIds && filters.assigneeIds.length > 0) {
      const setIds = new Set(filters.assigneeIds.map(String));
      return roomMembers.filter((m) => setIds.has(m.id));
    }
    return roomMembers;
  }, [filters.assigneeIds, roomMembers]);

  // リマインダー管理・自動作成管理ダイアログ用に完了・非表示を含む全タスクを取得
  const fetchAllTasksIncludingHidden = useCallback(async () => {
    if (!activeListId || activeListId === "0") {
      setAllTasksForDialogs([]);
      return;
    }

    try {
      // 現在のアクティブなプロジェクトの完了・非表示を含むタスクを取得
      const response = await fetch(
        `/api/todolists?roomId=${roomId}&honbuId=${honbuId}&includeHidden=true`,
      );
      if (!response.ok) throw new Error("Failed to fetch all tasks");
      const data = await response.json();

      // 全リストから全タスクを抽出
      const extractTasks = (tasks: any[]): Task[] => {
        const result: Task[] = [];
        for (const task of tasks) {
          const mappedTask = {
            id: task.taskId.toString(),
            type: task.type,
            title: task.title,
            completed: task.completed,
            status: task.status || "未対応",
            priority: task.priority || "なし",
            startDate: task.startDate || "",
            endDate: task.endDate || "",
            createdAt: task.createdAt || new Date(0).toISOString(),
            lastUpdateAt: task.lastUpdateAt || new Date(0).toISOString(),
            assignees: (task.taskAssignees || []).map((a: any) => ({
              id: a.assigneeHonbuId.toString(),
              name: a.name,
            })),
            requesters: (task.taskRequesters || []).map((r: any) => ({
              id: r.requesterHonbuId.toString(),
              name: r.name,
            })),
            tags: (task.taskTags || []).map((t: any) => ({
              id: Number(t.tagId),
              name: t.name,
              color: t.color ?? "",
            })),
            memo: task.memo || "",
            notification: task.notification,
            starred: task.starred,
            reminders: (task.Reminders || task.reminders || []).map(
              (r: any) => ({
                reminderId: (r.ReminderId || r.reminderId).toString(),
                taskId: (r.TaskId || r.taskId).toString(),
                reminderDateTime: r.ReminderDateTime || r.reminderDateTime,
              }),
            ),
            recurrenceRules: (Array.isArray(task.recurrenceRules)
              ? task.recurrenceRules
              : Array.isArray(task.RecurringRules)
                ? task.RecurringRules
                : []
            ).map((r: any) => ({
              // 大文字小文字、Rの数など、考えられる全てのパターンを許容するようにマッピング
              recurringRuleId: r.recurringRuleId ?? r.RecurringRuleId ?? r.id,
              recurrenceType: r.recurrenceType ?? r.RecurrenceType,
              // 数値の 0 を許容するため ?? を使用（|| だと 0 が false 扱いになるため）
              recurrenceValue: r.recurrenceValue ?? r.RecurrenceValue,
              periodStartDate: r.periodStartDate ?? r.PeriodStartDate,
              periodEndDate: r.periodEndDate ?? r.PeriodEndDate,
              dueDateType: r.dueDateType ?? r.DueDateType,
              dueDateRelativeDays:
                r.dueDateRelativeDays ?? r.DueDateRelativeDays,
              dueDateNextDayOfMonth:
                r.dueDateNextDayOfMonth ?? r.DueDateNextDayOfMonth,
              isDueDateDuration: r.isDueDateDuration ?? r.IsDueDateDuration,
              dueDateRelativeDaysEnd:
                r.dueDateRelativeDaysEnd ?? r.DueDateRelativeDaysEnd,
              dueDateNextDayOfMonthEnd:
                r.dueDateNextDayOfMonthEnd ?? r.DueDateNextDayOfMonthEnd,
              notificationEnabled:
                r.notificationEnabled ?? r.NotificationEnabled,
              reminderType: r.reminderType ?? r.ReminderType,
              reminderRelativeDays:
                r.reminderRelativeDays ?? r.ReminderRelativeDays,
              reminderNextDayOfMonth:
                r.reminderNextDayOfMonth ?? r.ReminderNextDayOfMonth,
              contents: r.contents ?? r.Contents,
            })),
            childTasks: task.childTasks ? extractTasks(task.childTasks) : [],
            completedAt: task.completedAt || null,
            completedBy: task.completedBy
              ? {
                  id: task.completedBy.assigneeHonbuId.toString(),
                  name: task.completedBy.name,
                }
              : undefined,
            createdHonbuId: task.createdHonbuId,
            sourceRecurringRuleId: task.sourceRecurringRuleId,
          };

          result.push(mappedTask);
        }
        return result;
      };

      // 現在のアクティブなプロジェクトのタスクのみを抽出
      const activeList = data.lists.find(
        (list: any) => list.todoListId.toString() === activeListId,
      );

      if (activeList) {
        const allTasks = extractTasks(activeList.tasks);
        setAllTasksForDialogs(allTasks);
      } else {
        setAllTasksForDialogs([]);
      }
    } catch (error) {
      console.error("Failed to fetch all tasks:", error);
      setAllTasksForDialogs([]);
    }
  }, [roomId, honbuId, activeListId]);

  // ダイアログ用のforceReload（allTasksForDialogsも更新）
  const forceReloadWithDialogTasks = useCallback(async () => {
    await forceReload();
    await fetchAllTasksIncludingHidden();
  }, [forceReload, fetchAllTasksIncludingHidden]);

  // ダイアログ用にラップされた関数（更新後に自動的にリロード）
  const updateTaskForDialog = useCallback(
    async (taskId: string, updates: TaskUpdateDto) => {
      await updateTask(taskId, updates);
      await forceReloadWithDialogTasks();
    },
    [updateTask, forceReloadWithDialogTasks],
  );

  const addReminderForDialog = useCallback(
    async (taskId: string, reminderDateTime: Date) => {
      const result = await addReminder(taskId, reminderDateTime);
      await forceReloadWithDialogTasks();
      return result;
    },
    [addReminder, forceReloadWithDialogTasks],
  );

  const deleteReminderForDialog = useCallback(
    async (reminderId: string | number) => {
      const result = await deleteReminder(reminderId);
      await forceReloadWithDialogTasks();
      return result;
    },
    [deleteReminder, forceReloadWithDialogTasks],
  );

  const updateTaskStatusForDialog = useCallback(
    async (taskId: string, status: Task["status"]) => {
      updateTaskStatus(taskId, status);
      await forceReloadWithDialogTasks();
    },
    [updateTaskStatus, forceReloadWithDialogTasks],
  );

  // 繰り返し作成管理ダイアログを開く
  const handleOpenRecurrenceManagement = useCallback(
    async (selectedTaskId?: string, selectedRuleId?: number) => {
      setRecurrenceInitialTaskId(selectedTaskId);
      setRecurrenceInitialRuleId(selectedRuleId);
      setShowRecurrenceManagement(true);
    },
    [],
  );

  // 自動作成設定の同期プロンプト
  const handleRecurrenceUpdateNeeded = useCallback(
    (info: { ruleId: number; task: Task; isOriginal: boolean }) => {
      setRecurrenceUpdatePrompt(info);
    },
    [],
  );

  const handleRecurrenceUpdateConfirm = useCallback(async () => {
    if (!recurrenceUpdatePrompt) return;
    setRecurrenceUpdateLoading(true);
    const { ruleId, task, customColumnValues } = recurrenceUpdatePrompt;
    const contents = JSON.stringify({
      id: task.id, // サブタスクのカスタム項目値を DB から補完するためのひも付け用
      title: task.title,
      memo: task.memo || "",
      priority: task.priority,
      notification: task.notification,
      assignees: (task.assignees || []).map((a) => ({
        id: parseInt(a.id),
        name: a.name,
      })),
      requesters: (task.requesters || []).map((r) => ({
        id: parseInt(r.id),
        name: r.name,
      })),
      tags: (task.tags || []).map((t) => ({
        id: t.id,
        name: t.name,
        color: t.color,
      })),
      childTasks: buildChildTasksForContents(task.childTasks || []),
      customColumnValues: (customColumnValues || [])
        .filter((v) => v.value != null && v.value !== "")
        .map((v) => ({ customColumnId: v.customColumnId, value: v.value })),
    });
    await updateRecurrenceContents(ruleId, contents);
    setRecurrenceUpdateLoading(false);
    setRecurrenceUpdatePrompt(null);
  }, [recurrenceUpdatePrompt, updateRecurrenceContents]);

  const handleRecurrenceUpdateDismiss = useCallback(() => {
    setRecurrenceUpdatePrompt(null);
  }, []);

  // タスクのソート
  const sortedTasks = useMemo(() => {
    const statusOrder: { [k in Task["status"]]: number } = {
      対応中: 1,
      未対応: 2,
      待機: 3,
      完了: 4,
      非表示: 5,
    };
    const priorityOrder: { [k in Task["priority"]]: number } = {
      高: 1,
      中: 2,
      低: 3,
      なし: 4,
    };
    return [...filteredTasks].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "status") {
        cmp = (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
      } else if (sortKey === "dueDate") {
        const da = a.startDate ? new Date(a.startDate).getTime() : Infinity;
        const db = b.startDate ? new Date(b.startDate).getTime() : Infinity;
        cmp = da - db;
      } else if (sortKey === "priority") {
        cmp =
          (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
      } else {
        cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [filteredTasks, sortKey, sortDirection]);

  // Myフィルター取得
  useEffect(() => {
    (async () => {
      if (honbuId === null) return;
      try {
        const r = await fetch(`/api/myfilters?honbuId=${honbuId}`);
        if (r.ok) {
          const data = await r.json();
          setMyFilters(data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setAreFiltersLoading(false);
      }
    })();
  }, [honbuId]);

  // タグ取得（ルーム変更時）
  useEffect(() => {
    setAvailableTags([]); // ルーム変更時に一旦クリア
    setAreTagsLoading(true);
    if (roomId === null) return;
    (async () => {
      try {
        const r = await fetch(`/api/tags?roomId=${roomId}&honbuId=${honbuId}`);
        if (!r.ok) throw new Error("Failed tags");
        const data = await r.json();
        setAvailableTags(
          data.map((t: any) => ({
            id: Number(t.tagId),
            name: t.name,
            color: t.color ?? "#cccccc",
          })),
        );
      } catch (e) {
        console.error(e);
        setAvailableTags([]);
      } finally {
        setAreTagsLoading(false);
      }
    })();
  }, [roomId, honbuId]);

  // タスクからタグをマージ（roomId≠0のみ）
  useEffect(() => {
    if (areTagsLoading) return;
    if (roomId === 0) return;
    const taskTags = new Map<number, Tag>();
    todoLists.forEach((l) =>
      l.tasks.forEach((t) =>
        (t.tags || []).forEach((tag) => {
          const idNum = Number(tag.id);
          if (!taskTags.has(idNum)) {
            taskTags.set(idNum, {
              id: idNum,
              name: tag.name,
              color: tag.color ?? "#cccccc",
            });
          }
        }),
      ),
    );
    if (taskTags.size === 0) return;
    setAvailableTags((prev) => {
      const existing = new Set(prev.map((p) => p.id));
      const merged = [...prev];
      taskTags.forEach((tag) => {
        if (!existing.has(tag.id)) merged.push(tag);
      });
      return merged;
    });
  }, [todoLists, areTagsLoading, roomId]);

  // ルームメンバー取得
  useEffect(() => {
    if (roomId == null) return;
    setRoomMembers([]);
    setAreMembersLoading(true);
    const ac = new AbortController();
    const currentRoom = roomId;
    (async () => {
      try {
        const r = await fetch(`/api/rooms/${currentRoom}/members`, {
          signal: ac.signal,
        });
        if (!r.ok) throw new Error("Failed members");
        const data = await r.json();
        if (ac.signal.aborted || currentRoom !== roomId) return;
        setRoomMembers(
          data.map((m: any) => ({ id: String(m.honbuId), name: m.name })),
        );
      } catch (e) {
        if (!ac.signal.aborted) {
          console.error(e);
          setRoomMembers([]);
        }
      } finally {
        if (!ac.signal.aborted) setAreMembersLoading(false);
      }
    })();
    return () => ac.abort();
  }, [roomId, honbuId]);

  // 詳細パネルの選択タスク自動設定
  useEffect(() => {
    if (isWideScreen && viewMode === "list") {
      if (sortedTasks.length > 0) {
        // 新規作成されたタスクがあれば優先的に選択（リストビューで作成された場合のみ）
        if (
          pendingSelectTaskIdRef.current &&
          pendingSelectTaskIdRef.current.view === "list"
        ) {
          const newTaskId = pendingSelectTaskIdRef.current.taskId;
          const newTask = sortedTasks.find((t) => t.id === newTaskId);
          if (newTask) {
            setSelectedDetail({ parent: newTask });
            pendingSelectTaskIdRef.current = null;

            // 新規作成されたタスクの位置までスクロール
            requestAnimationFrame(() => {
              const taskElement = document.querySelector(
                `[data-task-id="${newTaskId}"]`,
              );
              if (taskElement && listScrollContainerRef.current) {
                taskElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            });
            return;
          }
        }

        const currentParentId = selectedDetail?.parent.id;
        const freshParent = sortedTasks.find((t) => t.id === currentParentId);

        if (selectedDetail && !freshParent) {
          setSelectedDetail(null);
        } else if (selectedDetail && freshParent && freshParent !== selectedDetail.parent) {
          setSelectedDetail((prev) => (prev ? { ...prev, parent: freshParent } : null));
        }
      } else {
        setSelectedDetail(null);
      }
    }
  }, [sortedTasks, isWideScreen, selectedDetail, viewMode]);

  // コピーで作成されたタスクを詳細パネルに選択・表示する（list / taskmap、幅広・幅狭の両方）
  // 上の選択効果より後に実行することで、選択中タスクの更新処理に上書きされず新タスクが選択される
  useEffect(() => {
    const pending = pendingSelectCopiedTaskIdRef.current;
    if (!pending) return;
    if (viewMode !== "list" && viewMode !== "taskmap") return;
    if (pending.view !== viewMode) return;
    const newTask = sortedTasks.find((t) => t.id === pending.taskId);
    if (!newTask) return;
    setSelectedDetail({ parent: newTask });
    pendingSelectCopiedTaskIdRef.current = null;
    // 幅狭ではオーバーレイで詳細パネルを表示する
    if (!isWideScreen) setIsDetailOverlayOpen(true);
    requestAnimationFrame(() => {
      const taskElement = document.querySelector(
        `[data-task-id="${pending.taskId}"]`,
      );
      if (taskElement) {
        taskElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  }, [sortedTasks, isWideScreen, viewMode]);

  // サブタスク作成後に詳細パネルを新サブタスクに切り替え（リスト・ツリービュー）
  useEffect(() => {
    if (!pendingSelectSubtaskRef.current || !isWideScreen) return;
    const { parentTaskId, subtaskId } = pendingSelectSubtaskRef.current;
    const parent = sortedTasks.find((t) => t.id === parentTaskId);
    if (parent) {
      const subtask = findTaskById(parent.childTasks || [], subtaskId);
      if (subtask) {
        setSelectedDetail({ parent, child: subtask });
        pendingSelectSubtaskRef.current = null;
      }
    }
  }, [sortedTasks, isWideScreen, findTaskById]);

  // テーブルビュー・タスクマップビューで新規作成されたタスクにスクロール
  useEffect(() => {
    if (
      pendingSelectTaskIdRef.current &&
      (pendingSelectTaskIdRef.current.view === "table" ||
        pendingSelectTaskIdRef.current.view === "taskmap") &&
      pendingSelectTaskIdRef.current.view === viewMode
    ) {
      const newTaskId = pendingSelectTaskIdRef.current.taskId;
      const newTask = sortedTasks.find((t) => t.id === newTaskId);

      if (newTask) {
        pendingSelectTaskIdRef.current = null;

        requestAnimationFrame(() => {
          const taskElement = document.querySelector(
            `[data-task-id="${newTaskId}"]`,
          );
          if (taskElement) {
            taskElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });
      }
    }
  }, [sortedTasks, viewMode]);

  // ビュー切替時に詳細パネルをリセットし、保留中のスクロール情報をクリア
  useEffect(() => {
    setSelectedDetail(null);
    // 別のビューに切り替えたら、保留中のタスクIDをクリア
    if (
      pendingSelectTaskIdRef.current &&
      pendingSelectTaskIdRef.current.view !== viewMode
    ) {
      pendingSelectTaskIdRef.current = null;
    }
  }, [viewMode]);

  // ビュー切替時に詳細パネルをリセット
  useEffect(() => {
    setSelectedDetail(null);
  }, [viewMode]);

  // activeListId（プロジェクト）が変更されたら保存されたビューを復元
  useEffect(() => {
    if (activeListId) {
      const savedView = localStorage.getItem(
        `lastViewMode_${honbuId}_${roomId}_${activeListId}`,
      );
      if (
        savedView &&
        ["list", "table", "timeline", "board", "taskmap", "dashboard"].includes(
          savedView,
        )
      ) {
        // テーブルビューはダッシュボードに統合されたため読み替える
        setViewMode(
          savedView === "table"
            ? "dashboard"
            : (savedView as
                | "list"
                | "timeline"
                | "board"
                | "taskmap"
                | "dashboard"),
        );
      } else {
        // 保存された値がない場合はデフォルトのリストビューに戻す
        setViewMode("list");
      }
    }
  }, [activeListId, honbuId, roomId]);

  // viewMode を変更する専用の関数（保存処理を含む）
  const handleViewModeChange = useCallback(
    (
      newMode:
        | "list"
        | "table"
        | "timeline"
        | "board"
        | "taskmap"
        | "dashboard",
    ) => {
      setViewMode(newMode);
      if (activeListId) {
        localStorage.setItem(
          `lastViewMode_${honbuId}_${roomId}_${activeListId}`,
          newMode,
        );
      }
    },
    [activeListId, honbuId, roomId],
  );

  const handleCloseGeneralSettings = (settingsChanged: boolean) => {
    setShowGeneralSettingsDialog(false);
    if (settingsChanged) {
      // カスタム列の追加・変更を各ビューへ即時反映する
      refreshCustomColumns();
      setCustomColumnsVersion((v) => v + 1);
      // 設定が変更されていた場合、ステータスを再取得し、タスクリストも更新する
      const fetchStatusesAndRefresh = async () => {
        if (roomId === null) return;
        try {
          const response = await fetch(
            `/api/rooms/${roomId}/statuses?honbuId=${honbuId}`,
          );
          if (response.ok) {
            setRoomStatuses(await response.json());
            // useTodosフックから取得したrefreshTodosを呼び出してタスクリストを更新
            refreshTodos();
          }
        } catch (error) {
          console.error(
            "Failed to fetch room statuses after settings change:",
            error,
          );
        }
      };
      fetchStatusesAndRefresh();
    }
  };

  // タスク詳細選択時の処理
  const handleSelectDetail = (parent: Task, child?: Task) => {
    setSelectedDetail({ parent, child });
    if (!isWideScreen && (viewMode === "list" || viewMode === "taskmap")) {
      setIsDetailOverlayOpen(true);
    }
  };

  // サブタスク作成ラッパー：作成後にリスト・ツリービューの詳細パネルを新サブタスクに切り替え
  const handleAddSubtask = useCallback(
    async (parentTaskId: string, title: string) => {
      const newSubtaskId = await addSubtask(parentTaskId, title);
      if (
        newSubtaskId &&
        isWideScreen &&
        (viewMode === "list" || viewMode === "taskmap")
      ) {
        pendingSelectSubtaskRef.current = {
          parentTaskId,
          subtaskId: newSubtaskId,
        };
      }
      return newSubtaskId;
    },
    [addSubtask, isWideScreen, viewMode],
  );

  // タスクを別プロジェクトにコピー後、そのプロジェクトに切り替える
  const handleCopyToOtherList = useCallback(
    (targetListId: number, newTaskId?: string) => {
      setSelectedDetail(null);
      setIsDetailOverlayOpen(false);
      setModalTask(null);
      setActiveListId(String(targetListId));
      // 切り替え先プロジェクトでコピーしたタスクを選択・表示する
      if (newTaskId) {
        pendingSelectCopiedTaskIdRef.current = {
          taskId: newTaskId,
          view: viewMode,
        };
      }
      forceReload();
    },
    [setActiveListId, forceReload, viewMode],
  );

  // 同一プロジェクト内コピー完了後、作成されたタスクを詳細パネルに選択・表示する
  const handleTaskCopiedInList = useCallback(
    (newTaskId: string) => {
      pendingSelectCopiedTaskIdRef.current = {
        taskId: newTaskId,
        view: viewMode,
      };
      forceReload();
    },
    [forceReload, viewMode],
  );

  // コピー先候補プロジェクト一覧（id を数値に変換）
  const copyTargetLists = useMemo(
    () => visibleLists.map((l) => ({ id: Number(l.id), name: l.name })),
    [visibleLists],
  );

  // 新規タスク作成ダイアログを開く
  const handleOpenNewTaskDialog = (
    initialStatusOrEvent?: Task["status"] | React.MouseEvent,
  ) => {
    const canAddTask = activeListId && activeListId !== "0";

    if (canAddTask) {
      const initialStatus =
        typeof initialStatusOrEvent === "string"
          ? (initialStatusOrEvent as Task["status"])
          : undefined;
      setEditingTask(null);
      setInitialStatusForNewTask(initialStatus);
      setShowTaskDialog(true);
    } else {
      setShowCreatePrompt(true);
      setShowCreateListDialog(true);
    }
  };

  // タスク保存処理（新規・編集・一括）
  const handleSaveTask = async (formData: any, isBulk: boolean) => {
    const memberNameToId = new Map(roomMembers.map((m) => [m.name, m.id]));
    const nameToMemberIds = (names: string[]) =>
      (names || [])
        .map((n) => parseInt(memberNameToId.get(n) || "0", 10))
        .filter((v) => v !== 0);
    const nameToTagIds = (names: string[]) =>
      (names || [])
        .map((n) => availableTags.find((t) => t.name === n)?.id || 0)
        .filter((v) => v !== 0);

    if (isBulk) {
      if (formData.type === "single") {
        const bulkRows: {
          task: TaskCreateDto;
          assigneeNames: string[];
        }[] = [];
        const multiRows: {
          task: TaskCreateDto;
          assigneeNames: string[];
        }[] = [];

        formData.rows.forEach((row: any) => {
          const todoListIdNum = activeListId ? parseInt(activeListId, 10) : 0;
          const tagIds = nameToTagIds(row.tags || []);
          const notification = !!row.notification;
          const priority = row.priority || "なし";
          const assigneeIds = nameToMemberIds(row.assignees);
          const requesterIds = nameToMemberIds(row.requesters);

          const validSub = (row.subtasks || []).filter(
            (s: any) => s && s.title && s.title.trim() !== "",
          );

          if (validSub.length === 0) {
            // 純粋な single タスク（bulk で一括送信）
            const task: TaskCreateDto = {
              title: row.title,
              todoListId: todoListIdNum,
              tagIds,
              startDate: row.startDate || undefined,
              endDate: row.endDate || undefined,
              memo: undefined,
              notification,
              priority,
              createdHonbuId: honbuId,
              roomId: roomId,
              type: "single",
              assigneeHonbuIds: assigneeIds,
              requesterHonbuIds: requesterIds,
            };
            bulkRows.push({ task, assigneeNames: row.assignees || [] });
          } else {
            // サブタスクあり → multi として個別送信（childTasks を含める）
            const childTasks: SubTaskCreateDto[] = validSub.map((st: any) => ({
              title: st.title,
              assigneeHonbuIds: nameToMemberIds(st.assignees),
              requesterHonbuIds: nameToMemberIds(st.requesters),
              startDate: st.startDate || undefined,
              endDate: st.endDate || undefined,
              memo: undefined,
              childTasks: [],
            }));
            const task: TaskCreateDto = {
              title: row.title,
              todoListId: todoListIdNum,
              tagIds,
              startDate: row.startDate || undefined,
              endDate: row.endDate || undefined,
              memo: undefined,
              notification,
              priority,
              createdHonbuId: honbuId,
              roomId: roomId,
              type: "multi",
              childTasks,
              assigneeHonbuIds: assigneeIds,
              requesterHonbuIds: requesterIds,
            };
            multiRows.push({ task, assigneeNames: row.assignees || [] });
          }
        });

        // まず bulkRows は addBulkTasks でまとめて送る（効率）
        if (bulkRows.length > 0) addBulkTasks(bulkRows);
        // サブタスク付きの親は個別に API 呼び出し（addTask）して保存
        if (multiRows.length > 0) {
          await Promise.all(
            multiRows.map((item) => addTask(item.task, item.assigneeNames)),
          );
        }
      } else {
        const mapSubtasks = (
          subtasks: EditingChildTask[],
        ): SubTaskCreateDto[] => {
          if (!subtasks || subtasks.length === 0) {
            return [];
          }
          return subtasks
            .filter((st) => st.title.trim() !== "")
            .map((st: EditingChildTask) => ({
              title: st.title,
              assigneeHonbuIds: nameToMemberIds(st.assignees),
              requesterHonbuIds: nameToMemberIds(st.requesters),
              startDate: st.startDate || undefined,
              endDate: st.endDate || undefined,
              memo: undefined,
              childTasks: [],
            }));
        };

        const parentTasksToCreate: {
          task: TaskCreateDto;
          assigneeNames: string[];
        }[] = formData.rows
          .filter((row: any) => row.title.trim() !== "")
          .map((row: any) => {
            const validSubtasks = (row.subtasks || []).filter(
              (st: any) => st.title && st.title.trim() !== "",
            );

            const hasSubtasks = validSubtasks.length > 0;

            const parentTask: TaskCreateDto = {
              title: row.title,
              todoListId: activeListId ? parseInt(activeListId, 10) : 0,
              tagIds: nameToTagIds(row.tags),
              startDate: undefined,
              endDate: undefined,
              memo: undefined,
              notification: row.notification,
              priority: row.priority,
              createdHonbuId: honbuId,
              roomId: roomId,
              type: hasSubtasks ? "multi" : "single",
              childTasks: hasSubtasks
                ? validSubtasks.map((st: any) => ({
                    title: st.title,
                    assigneeHonbuIds: nameToMemberIds(st.assignees),
                    requesterHonbuIds: nameToMemberIds(st.requesters),
                    startDate: st.startDate || undefined,
                    endDate: st.endDate || undefined,
                    memo: undefined,
                    childTasks: [],
                  }))
                : [],
              assigneeHonbuIds: nameToMemberIds(row.assignees),
              requesterHonbuIds: nameToMemberIds(row.requesters),
            };
            return {
              task: parentTask,
              assigneeNames: row.assignees || [],
            };
          });
        await Promise.all(
          parentTasksToCreate.map((item) =>
            addTask(item.task, item.assigneeNames),
          ),
        );
      }
    } else if (editingTask) {
      const taskData: TaskUpdateDto = {
        title: formData.title,
        tagIds: nameToTagIds(formData.tags),
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        memo: formData.memo || undefined,
        notification: formData.notification,
        priority: formData.priority,
        type: formData.type,
        assigneeHonbuIds: [],
        requesterHonbuIds: [],
      };
      if (formData.type === "single") {
        taskData.assigneeHonbuIds = nameToMemberIds(formData.assignees);
        taskData.requesterHonbuIds = nameToMemberIds(formData.requesters);
      }
      updateTask(editingTask.id, taskData);
    } else {
      const taskData: TaskCreateDto = {
        title: formData.title,
        todoListId: activeListId ? parseInt(activeListId, 10) : 0,
        tagIds: nameToTagIds(formData.tags),
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        memo: formData.memo || undefined,
        notification: formData.notification,
        priority: formData.priority,
        createdHonbuId: honbuId,
        roomId: roomId,
        type: formData.type,
        status: formData.status,
        // F017: テンプレート由来の既存添付ファイルへリンクする
        fileIds: formData.fileIds || [],
      };
      if (formData.type === "single") {
        taskData.assigneeHonbuIds = nameToMemberIds(formData.assignees);
        taskData.requesterHonbuIds = nameToMemberIds(formData.requesters);
      } else {
        taskData.childTasks = formData.childTasks.map((c: any) => ({
          title: c.title,
          assigneeHonbuIds: nameToMemberIds(c.assignees),
          requesterHonbuIds: nameToMemberIds(c.requesters),
          startDate: c.startDate || undefined,
          endDate: c.endDate || undefined,
          memo: c.memo || undefined,
          customColumnValues: (c.customColumnValues ?? []).filter(
            (v: { value: string }) => v.value !== "",
          ),
        }));
      }
      const newTaskId = await addTask(taskData, formData.assignees || []);
      if (newTaskId) {
        pendingSelectTaskIdRef.current = { taskId: newTaskId, view: viewMode };
        await saveCustomColumnValues(newTaskId, formData.customColumnValues);
        // F017: ダイアログで選択されたファイルをタスク作成後にアップロードする
        await uploadPendingTaskFiles(newTaskId, formData.pendingFiles);
      }
    }
    setShowTaskDialog(false);
    setEditingTask(null);
  };

  // F017: 新規作成したタスクへダイアログで選択されたファイルをアップロードする
  const uploadPendingTaskFiles = async (taskId: string, files?: File[]) => {
    if (!files || files.length === 0) return;
    try {
      for (const f of files) {
        await uploadTaskFile(taskId, honbuId, f);
      }
    } catch (e) {
      console.error("Failed to upload attachments:", e);
    }
    await forceReload();
  };

  // 新規作成したタスクにカスタム項目の値を保存する
  const saveCustomColumnValues = async (
    taskId: string,
    values?: { customColumnId: number; value: string }[],
  ) => {
    if (!values || values.length === 0) return;
    await Promise.all(
      values.map((c) =>
        fetch(
          `/api/tasks/${taskId}/custom-column-values/${c.customColumnId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value: c.value }),
          },
        ).catch(() => {}),
      ),
    );
    refreshCustomColumnValues();
  };

  // タグ名からPaletteColorを取得（不正な値は"Blue"にフォールバック）
  const getPaletteColor = (tagName: string): PaletteColor => {
    // PaletteColorのキーのリスト（型安全性を高めるため）
    const validColors: PaletteColor[] = [
      "Blue",
      "Skyblue",
      "Lightgreen",
      "Green",
      "Orange",
      "Red",
      "Pink",
      "Lavender",
      "Purple",
    ];

    // tagNameがPaletteColorのいずれかと一致するかチェック
    if ((validColors as string[]).includes(tagName)) {
      return tagName as PaletteColor;
    }

    // 一致しない場合はデフォルトの"Blue"を返す
    return "Blue";
  };

  // 新規リスト作成
  // const handleCreateList = (name: string) => {
  //   const listData: TodoListCreateDto = {
  //     name,
  //     roomId,
  //     createdHonbuId: honbuId,
  //   };
  //   addTodoList(listData);
  // };

  // タグ追加
  const handleAddTag = async (name: string, color: string) => {
    if (!name.trim()) return;
    try {
      const res = await fetch("/api/tags", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, color, roomId, createdHonbuId: honbuId }),
      });
      if (!res.ok) throw new Error("create tag failed");
      const r = await fetch(`/api/tags?roomId=${roomId}&honbuId=${honbuId}`);
      if (r.ok) {
        const data = await r.json();
        setAvailableTags(
          data.map((t: any) => ({
            id: Number(t.tagId),
            name: t.name,
            color: t.color ?? "#cccccc",
          })),
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  // タグ削除
  const handleDeleteTag = async (tagId: number) => {
    try {
      const r = await fetch(`/api/tags/${tagId}`, { method: "DELETE" });
      if (!r.ok) throw new Error("delete tag failed");
      const r2 = await fetch(`/api/tags?roomId=${roomId}&honbuId=${honbuId}`);
      if (r2.ok) {
        const data = await r2.json();
        setAvailableTags(
          data.map((t: any) => ({
            id: Number(t.tagId),
            name: t.name,
            color: t.color ?? "#cccccc",
          })),
        );
      } else {
        setAvailableTags((p) => p.filter((t) => t.id !== tagId));
      }
    } catch (e) {
      console.error(e);
    }
  };

  // タグ更新
  const handleUpdateTag = async (
    tagId: number,
    name: string,
    color: string,
  ) => {
    try {
      const res = await fetch(`/api/tags/${tagId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, color }),
      });
      if (!res.ok) throw new Error("update tag failed");
      const r = await fetch(`/api/tags?roomId=${roomId}&honbuId=${honbuId}`);
      if (r.ok) {
        const data = await r.json();
        setAvailableTags(
          data.map((t: any) => ({
            id: Number(t.tagId),
            name: t.name,
            color: t.color ?? "#cccccc",
          })),
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Myフィルター適用トグル
  const handleApplyMyFilterToggle = (filterId: number) => {
    const activating = activeMyFilterId !== filterId;
    setActiveMyFilterId(activating ? filterId : null);
    const simple = {
      myTasksOnly: filters.myTasksOnly,
      myRequestsOnly: filters.myRequestsOnly,
      dueTodayOnly: filters.dueTodayOnly,
      starredOnly: filters.starredOnly,
    };
    if (activating) {
      const f = myFilters.find((mf) => mf.filterId === filterId);
      if (f) {
        const saved = JSON.parse(f.filterConditions);
        setFilters({ ...initialFilterState, ...saved, ...simple });
      }
    } else {
      setFilters({ ...initialFilterState, ...simple });
    }
  };

  // Myフィルター保存
  const handleSaveMyFilter = async (name: string, conditions: FilterState) => {
    try {
      const r = await fetch("/api/myfilters", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          honbuId,
          filterName: name,
          filterConditions: conditions,
        }),
      });
      if (r.ok) {
        const nf = await r.json();
        setMyFilters((p) => [...p, nf]);
        setShowDetailedFilter(false);
        setActiveMyFilterId(nf.filterId); // ← ここでON
        setFilters({ ...initialFilterState, ...conditions }); // ← ここで内容反映
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Myフィルター更新
  const handleUpdateMyFilter = async (
    id: number,
    name: string,
    conditions: FilterState,
  ) => {
    try {
      const r = await fetch(`/api/myfilters/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filterName: name,
          filterConditions: conditions,
        }),
      });
      if (r.ok) {
        setMyFilters((p) =>
          p.map((f) =>
            f.filterId === id
              ? {
                  ...f,
                  filterName: name,
                  filterConditions: JSON.stringify(conditions),
                }
              : f,
          ),
        );
        setShowDetailedFilter(false);
        setEditingMyFilter(null);
        setActiveMyFilterId(null);
        setTimeout(() => {
          setActiveMyFilterId(id);
          setFilters({ ...initialFilterState, ...conditions });
        }, 0);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Myフィルター削除
  const handleDeleteMyFilter = async (id: number) => {
    try {
      const r = await fetch(`/api/myfilters/${id}`, { method: "DELETE" });
      if (r.ok) {
        setMyFilters((p) => p.filter((f) => f.filterId !== id));
        if (activeMyFilterId === id) {
          setActiveMyFilterId(null);
          setFilters(initialFilterState);
        }
        setShowDetailedFilter(false);
        setEditingMyFilter(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Myフィルター編集開始
  const openMyFilterForEdit = (mf: MyFilter) => {
    setEditingMyFilter(mf);
    setShowDetailedFilter(true);
  };

  useEffect(() => {
    const isInitial =
      JSON.stringify(filters) === JSON.stringify(initialFilterState);
    if (isInitial && activeMyFilterId !== null) {
      setActiveMyFilterId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // フィルタートグルUI
  const FilterToggle = ({
    label,
    checked,
    onChange,
  }: {
    label: string;
    checked: boolean;
    onChange: () => void;
  }) => (
    <label
      className="flex items-center justify-between text-sm cursor-pointer pl-2"
      onClick={onChange}
    >
      <span>{label}</span>
      <div
        className={`w-11 h-6 rounded-full transition-colors ${
          checked ? "bg-green-500" : "bg-gray-300"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          onChange();
        }}
      >
        <div
          className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          } mt-0.5`}
        />
      </div>
    </label>
  );

  const handleUpdateListNameAndRefreshUI = async (
    listId: string,
    name: string,
  ): Promise<boolean> => {
    const previousTodoLists = [...todoLists];
    setTodoLists((currentLists) =>
      currentLists.map((list) =>
        list.id === listId ? { ...list, name: name.trim() } : list,
      ),
    );
    const success = await updateListName(listId, name);
    if (!success) {
      setTodoLists(previousTodoLists);
    }
    return success;
  };

  // 表示スタイルボタンのスタイル
  const activeViewButtonStyle = {
    backgroundColor: PaletteColorHex[color],
    backgroundImage:
      "linear-gradient(to bottom, rgba(255,255,255,0.15), rgba(255,255,255,0))",
    boxShadow:
      "inset 0 1px 2px rgba(255,255,255,0.25), 0 3px 5px rgba(0,0,0,0.15)",
  };

  const handleShowHiddenColumn = (status: "完了" | "非表示") => {
    fetchAndMergeTasks(status);
    if (status === "完了") {
      setFilters((prevFilters) => ({ ...prevFilters, showCompleted: true }));
    } else if (status === "非表示") {
      setFilters((prevFilters) => ({ ...prevFilters, showHidden: true }));
    }
  };

  useEffect(() => {
    if (filters.showCompleted) {
      fetchAndMergeTasks("完了");
    }
    if (filters.showHidden) {
      fetchAndMergeTasks("非表示");
    }
  }, [filters.showCompleted, filters.showHidden, fetchAndMergeTasks]);

  // テーブルビューからタスク追加時のハンドラー
  const handleAddTaskFromTable = async (title: string) => {
    if (!activeListId || activeListId === "0") {
      alert("タスクを追加するプロジェクトリストを選択してください。");
      return;
    }
    const taskData: TaskCreateDto = {
      title: title,
      todoListId: parseInt(activeListId, 10),
      status: "未対応",
      priority: "なし",
      tagIds: [],
      notification: false,
      createdHonbuId: honbuId,
      roomId: roomId,
      type: "single",
      assigneeHonbuIds: [],
      requesterHonbuIds: [],
    };
    const newTaskId = await addTask(taskData, []);
    if (newTaskId) {
      pendingSelectTaskIdRef.current = { taskId: newTaskId, view: "table" };
    }
  };
  // const rightHeaderRef = useRef<HTMLDivElement>(null);

  const [templateTarget, setTemplateTarget] = useState<TodoList | null>(null);

  const handleSaveProjectTemplate = async (
    name: string,
    isShare: boolean,
    includeCompleted: boolean,
    includeHidden: boolean,
  ) => {
    if (!templateTarget) return;
    try {
      const res = await fetch(
        `/api/todolists/${templateTarget.id}/save-as-template`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            templateName: name.trim(),
            isShare: isShare,
            createdHonbuId: honbuId,
            includeCompleted: includeCompleted,
            includeHidden: includeHidden,
          }),
        },
      );
      if (!res.ok) throw new Error();
      setTemplateTarget(null);
    } catch (e) {
      alert("保存に失敗しました");
    }
  };

  // ======== JSX（UI部分） ========
  return (
    // アプリ全体のレイアウト
    <div
      className="relative flex h-full bg-gray-50 font-sans overflow-hidden"
      style={{
        paddingRight: helpPanelWidth,
        transition: "padding-right 225ms ease-out",
      }}
    >
      {/*
        メインエリアラッパー: CSS transform によって position:fixed 子要素の基点を
        このラッパー内に限定する。ヘルプパネルを除いた領域にダイアログが収まるようにする。
      */}
      <div
        style={{
          flex: 1,
          display: "flex",
          height: "100%",
          overflow: "hidden",
          position: "relative",
          transform: "translate(0, 0)",
        }}
      >
        {templateTarget && (
          <SaveProjectTemplateModal
            initialName={templateTarget.name}
            onClose={() => setTemplateTarget(null)}
            onSave={handleSaveProjectTemplate}
            roomId={roomId}
          />
        )}
        {/* サイドバー折りたたみ時の展開ボタン */}
        {sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="absolute top-4 left-0 z-50 bg-white border border-gray-300 rounded-r-md shadow hover:bg-gray-100 px-1 py-2 flex items-center"
            aria-label="サイドバーを展開"
            title="サイドバーを展開"
          >
            <ChevronRightIcon fontSize="small" />
          </button>
        )}

        {/* サイドバー本体 */}
        <div
          className={`relative flex-shrink-0 transition-all duration-300 ${
            sidebarCollapsed ? "w-0 overflow-hidden" : "w-80"
          }`}
        >
          <div
            className={`absolute inset-0 bg-white border-r-2 border-gray-200 shadow-sm flex flex-col transition-transform duration-300 ${
              sidebarCollapsed ? "-translate-x-full" : "translate-x-0"
            }`}
          >
            {/* サイドバー内部 */}
            {!sidebarCollapsed && (
              <>
                <div className="flex-grow overflow-y-auto p-4">
                  <div className="flex justify-end mb-2">
                    <button
                      type="button"
                      onClick={() => setSidebarCollapsed(true)} // サイドバーを閉じる
                      aria-label="サイドバーを閉じる"
                      title="サイドバーを閉じる"
                      className="rounded-md hover:bg-gray-100 text-gray-600 active:scale-[0.97] transition flex items-center space-x-1"
                    >
                      <ChevronLeftIcon className="w-4 h-4" fontSize="small" />
                      <span className="text-sm">サイドバーを閉じる</span>
                    </button>
                  </div>
                  {/* プロジェクトヘッダー */}
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      onClick={() => setIsProjectListOpen(!isProjectListOpen)}
                      className="flex-1 text-left text-sm font-bold text-gray-700 p-1 rounded hover:bg-gray-100 cursor-pointer"
                      title="プロジェクトを折りたたむ/開く"
                    >
                      <span className="truncate">プロジェクト</span>
                    </button>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowCreatePrompt(false);
                          setShowCreateListDialog(true);
                        }}
                        aria-label="新規プロジェクト作成"
                        title="新規プロジェクト作成"
                        className="p-2 rounded-md hover:bg-gray-100 text-gray-600 active:scale-[0.97] transition"
                      >
                        <LibraryAddTwoToneIcon
                          className="w-4 h-4"
                          fontSize="small"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditListDialog();
                        }}
                        aria-label="プロジェクトを編集"
                        title="プロジェクトを編集"
                        className="p-2 rounded-md hover:bg-gray-100 text-gray-600 active:scale-[0.97] transition"
                      >
                        <DriveFileRenameOutlineTwoToneIcon
                          className="w-4 h-4"
                          fontSize="small"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsProjectListOpen(!isProjectListOpen);
                        }}
                        aria-label="プロジェクトを折りたたむ"
                        title="プロジェクトを折りたたむ"
                        className="p-2 rounded-md hover:bg-gray-100 text-gray-600 active:scale-[0.97] transition"
                      >
                        {isProjectListOpen ? (
                          <ExpandMoreIcon
                            className="w-4 h-4"
                            fontSize="small"
                          />
                        ) : (
                          <ChevronRightIcon
                            className="w-4 h-4"
                            fontSize="small"
                          />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* リスト一覧 */}
                  {isProjectListOpen && (
                    <div className="space-y-0.5">
                      {visibleLists.map((list) => {
                        const isPinned = projectPins.has(parseInt(list.id, 10));
                        const isActive = activeListId === list.id;
                        const showMenu = openMenuListId === list.id;

                        return (
                          <div key={list.id} className="relative">
                            <div
                              className={`group flex items-center justify-between px-2 py-1 rounded-lg cursor-pointer transition-colors ${
                                isActive
                                  ? "text-white"
                                  : "text-gray-700 hover:bg-gray-100"
                              }`}
                              style={
                                isActive
                                  ? {
                                      backgroundColor: PaletteColorHex[color],
                                      backgroundImage:
                                        "linear-gradient(to bottom, rgba(255,255,255,0.15), rgba(255,255,255,0))",
                                      boxShadow:
                                        "inset 0 1px 2px rgba(255,255,255,0.25), 0 3px 5px rgba(0,0,0,0.15)",
                                    }
                                  : {}
                              }
                              onClick={() => setActiveListId(list.id)}
                            >
                              {isPinned ? (
                                <PushPinIcon
                                  className={`ml-1 mr-2 ${
                                    activeListId === list.id
                                      ? "text-white"
                                      : "text-gray-500"
                                  }`}
                                  fontSize="small"
                                />
                              ) : (
                                <PlaylistAddCheckCircleTwoToneIcon
                                  className={`mr-2 ${
                                    activeListId === list.id
                                      ? "text-white"
                                      : "text-gray-500"
                                  }`}
                                />
                              )}
                              <span className="truncate flex-1 font-semibold">
                                {list.name}
                              </span>
                              <DeadlineCountBadges
                                overdue={
                                  deadlineCountsByList.get(list.id)?.overdue ?? 0
                                }
                                today={
                                  deadlineCountsByList.get(list.id)?.today ?? 0
                                }
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuListId(showMenu ? null : list.id);
                                }}
                                className={`p-1 rounded-full transition-opacity ${
                                  showMenu
                                    ? "opacity-100"
                                    : "opacity-0 group-hover:opacity-60"
                                } hover:bg-black/10`}
                                title="メニュー"
                              >
                                <MoreVertIcon
                                  className={`w-4 h-4 mb-1  ${
                                    isActive ? "text-white" : "text-gray-600"
                                  }`}
                                  fontSize="small"
                                />
                              </button>
                            </div>

                            {/* ドロップダウンメニュー */}
                            {showMenu && (
                              <>
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={() => setOpenMenuListId(null)}
                                />
                                <div className="absolute right-0 mt-1 w-50 bg-white rounded-md shadow-lg z-50 py-1 border border-gray-200">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleProjectPin(list.id);
                                      setOpenMenuListId(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    {isPinned ? (
                                      <>
                                        <PushPinIcon
                                          fontSize="small"
                                          className="text-gray-500"
                                        />
                                        <span>ピン留め解除（自分のみ）</span>
                                      </>
                                    ) : (
                                      <>
                                        <PushPinOutlinedIcon
                                          fontSize="small"
                                          className="text-gray-500"
                                        />
                                        <span>ピン留め（自分のみ）</span>
                                      </>
                                    )}
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateListStatus(list.id, "Hidden");
                                      setOpenMenuListId(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <VisibilityOffIcon
                                      fontSize="small"
                                      className="text-gray-500"
                                    />
                                    <span>非表示（全てのユーザー）</span>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTemplateTarget(list);
                                      setOpenMenuListId(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <SaveAltIcon
                                      fontSize="small"
                                      className="text-gray-500"
                                    />
                                    <span>テンプレート化</span>
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* ソート設定 */}
                  <div className="space-y-3 mt-4 pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setIsSortOpen(!isSortOpen)}
                        className="flex-1 text-left text-sm font-bold text-gray-700 p-1 rounded hover:bg-gray-100 cursor-pointer"
                        title="ソート設定を折りたたむ/開く"
                      >
                        <span className="truncate">ソート</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsSortOpen(!isSortOpen);
                        }}
                        aria-label="ソート設定を折りたたむ"
                        title="ソート設定を折りたたむ"
                        className="p-2 rounded-md hover:bg-gray-100 text-gray-600 active:scale-[0.97] transition"
                      >
                        {isSortOpen ? (
                          <ExpandMoreIcon
                            className="w-4 h-4"
                            fontSize="small"
                          />
                        ) : (
                          <ChevronRightIcon
                            className="w-4 h-4"
                            fontSize="small"
                          />
                        )}
                      </button>
                    </div>
                    {isSortOpen && (
                      <div className="flex items-center space-x-2">
                        <select
                          value={sortKey}
                          onChange={(e) =>
                            setSortKey(e.target.value as SortKey)
                          }
                          className="w-full p-2 border rounded text-sm"
                        >
                          <option value="createdAt">作成日順</option>
                          <option value="status">ステータス順</option>
                          <option value="dueDate">期限順</option>
                          <option value="priority">重要度順</option>
                        </select>
                        <button
                          onClick={() =>
                            setSortDirection((d) =>
                              d === "asc" ? "desc" : "asc",
                            )
                          }
                          className="p-2 border rounded hover:bg-gray-100"
                          aria-label="昇順/降順"
                          title="昇順/降順"
                        >
                          <SwapVertIcon
                            className={`w-4 h-4 ${
                              sortDirection === "desc"
                                ? "transform rotate-180"
                                : ""
                            }`}
                          />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* フィルター設定 */}
                  <div className="space-y-3 mt-4 pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className="flex-1 text-left text-sm font-bold text-gray-700 p-1 rounded hover:bg-gray-100 cursor-pointer"
                        title="フィルター設定を折りたたむ/開く"
                      >
                        <span className="truncate">フィルター</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsFilterOpen(!isFilterOpen);
                        }}
                        aria-label="フィルター設定を折りたたむ"
                        title="フィルター設定を折りたたむ"
                        className="p-2 rounded-md hover:bg-gray-100 text-gray-600 active:scale-[0.97] transition"
                      >
                        {isFilterOpen ? (
                          <ExpandMoreIcon
                            className="w-4 h-4"
                            fontSize="small"
                          />
                        ) : (
                          <ChevronRightIcon
                            className="w-4 h-4"
                            fontSize="small"
                          />
                        )}
                      </button>
                    </div>
                    {isFilterOpen && (
                      <>
                        {/* 名前検索 / タスクID検索（右のアイコンで切替） */}
                        <div className="relative mt-1">
                          <SearchIcon
                            className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                            fontSize="small"
                          />
                          <input
                            type="text"
                            value={filters.searchKeyword}
                            onChange={(e) =>
                              setFilters((f) => ({
                                ...f,
                                searchKeyword: e.target.value,
                              }))
                            }
                            placeholder={
                              filters.searchMode === "id"
                                ? "タスクIDで絞り込み（完全一致）"
                                : "メインタスク名で絞り込み"
                            }
                            className={`w-full pl-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 ${
                              filters.searchKeyword ? "pr-14" : "pr-8"
                            }`}
                          />
                          {filters.searchKeyword && (
                            <button
                              type="button"
                              onClick={() =>
                                setFilters((f) => ({ ...f, searchKeyword: "" }))
                              }
                              aria-label="検索をクリア"
                              title="検索をクリア"
                              className="absolute right-8 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100 text-gray-500"
                            >
                              <ClearIcon fontSize="small" className="w-4 h-4" />
                            </button>
                          )}
                          {/* 検索対象の切替: メインタスク名 ⇔ タスクID */}
                          <button
                            type="button"
                            onClick={() =>
                              setFilters((f) => ({
                                ...f,
                                searchMode: f.searchMode === "id" ? "name" : "id",
                                searchKeyword: "",
                              }))
                            }
                            aria-label="検索対象を切り替え"
                            title={
                              filters.searchMode === "id"
                                ? "タスクIDで絞り込み中（クリックでメインタスク名に切替）"
                                : "メインタスク名で絞り込み中（クリックでタスクIDに切替）"
                            }
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100 text-gray-500"
                          >
                            {filters.searchMode === "id" ? (
                              <TravelExploreIcon
                                fontSize="small"
                                className="w-4 h-4"
                              />
                            ) : (
                              <ManageSearchIcon
                                fontSize="small"
                                className="w-4 h-4"
                              />
                            )}
                          </button>
                        </div>
                        {/* 検索とトグルフィルターを薄い点線で区切る */}
                        <div className="border-t border-dashed border-gray-300" />
                        {roomId !== 0 && (
                          <>
                            <FilterToggle
                              label="自分が担当者"
                              checked={filters.myTasksOnly}
                              onChange={() =>
                                setFilters((f) => ({
                                  ...f,
                                  myTasksOnly: !f.myTasksOnly,
                                }))
                              }
                            />
                            <FilterToggle
                              label="自分が依頼者"
                              checked={filters.myRequestsOnly}
                              onChange={() =>
                                setFilters((f) => ({
                                  ...f,
                                  myRequestsOnly: !f.myRequestsOnly,
                                }))
                              }
                            />
                          </>
                        )}
                        <FilterToggle
                          label="期限が今日を含むタスク"
                          checked={filters.dueTodayOnly}
                          onChange={() =>
                            setFilters((f) => ({
                              ...f,
                              dueTodayOnly: !f.dueTodayOnly,
                            }))
                          }
                        />
                        <FilterToggle
                          label="期限切れのタスク"
                          checked={filters.overdueOnly}
                          onChange={() =>
                            setFilters((f) => ({
                              ...f,
                              overdueOnly: !f.overdueOnly,
                            }))
                          }
                        />
                        <FilterToggle
                          label="スター付きのタスク"
                          checked={filters.starredOnly}
                          onChange={() =>
                            setFilters((f) => ({
                              ...f,
                              starredOnly: !f.starredOnly,
                            }))
                          }
                        />
                        <FilterToggle
                          label="完了タスクを表示"
                          checked={filters.showCompleted}
                          onChange={() => {
                            const nextValue = !filters.showCompleted;
                            setFilters((f) => ({
                              ...f,
                              showCompleted: nextValue,
                            }));
                            if (nextValue) {
                              fetchAndMergeTasks("完了");
                            }
                          }}
                        />
                        <FilterToggle
                          label="非表示タスクを表示"
                          checked={filters.showHidden}
                          onChange={() => {
                            const nextValue = !filters.showHidden;
                            setFilters((f) => ({
                              ...f,
                              showHidden: nextValue,
                            }));
                            if (nextValue) {
                              fetchAndMergeTasks("非表示");
                            }
                          }}
                        />
                      </>
                    )}
                  </div>

                  {/* Myフィルター一覧 */}
                  <div className="mt-4 pt-2 border-t border-gray-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setIsMyFilterOpen(!isMyFilterOpen)}
                        className="flex-1 text-left text-sm font-bold text-gray-700 p-1 rounded hover:bg-gray-100 cursor-pointer"
                        title="Myフィルターを折りたたむ/開く"
                      >
                        <span className="truncate">Myフィルター</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMyFilterOpen(!isMyFilterOpen);
                        }}
                        aria-label="Myフィルターを折りたたむ"
                        title="Myフィルターを折りたたむ"
                        className="p-2 rounded-md hover:bg-gray-100 text-gray-600 active:scale-[0.97] transition"
                      >
                        {isMyFilterOpen ? (
                          <ExpandMoreIcon
                            className="w-4 h-4"
                            fontSize="small"
                          />
                        ) : (
                          <ChevronRightIcon
                            className="w-4 h-4"
                            fontSize="small"
                          />
                        )}
                      </button>
                    </div>
                    {isMyFilterOpen &&
                      myFilters.map((myFilter) => (
                        <div
                          key={myFilter.filterId}
                          className="flex items-center justify-between text-sm"
                        >
                          <button
                            type="button"
                            onClick={() =>
                              handleApplyMyFilterToggle(myFilter.filterId)
                            }
                            className="text-left flex-1 min-w-0 truncate px-1"
                            title={myFilter.filterName}
                          >
                            <span
                              className={`truncate ${
                                activeMyFilterId === myFilter.filterId
                                  ? "font-medium"
                                  : ""
                              }`}
                            >
                              {myFilter.filterName}
                            </span>
                          </button>

                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={() => openMyFilterForEdit(myFilter)}
                              className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-800"
                              aria-label="フィルター編集"
                              title="フィルター編集"
                            >
                              <SettingsIcon
                                className="w-4 h-4"
                                fontSize="small"
                              />
                            </button>
                            <FilterToggle
                              label=""
                              checked={activeMyFilterId === myFilter.filterId}
                              onChange={() =>
                                handleApplyMyFilterToggle(myFilter.filterId)
                              }
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                  {/* その他セクション */}
                  <div className="mt-4 pt-2 border-t border-gray-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setIsOtherOpen(!isOtherOpen)}
                        className="flex-1 text-left text-sm font-bold text-gray-700 p-1 rounded hover:bg-gray-100 cursor-pointer"
                        title="その他を折りたたむ/開く"
                      >
                        <span className="truncate">その他</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsOtherOpen(!isOtherOpen);
                        }}
                        aria-label="その他を折りたたむ"
                        title="その他を折りたたむ"
                        className="p-2 rounded-md hover:bg-gray-100 text-gray-600 active:scale-[0.97] transition"
                      >
                        {isOtherOpen ? (
                          <ExpandMoreIcon
                            className="w-4 h-4"
                            fontSize="small"
                          />
                        ) : (
                          <ChevronRightIcon
                            className="w-4 h-4"
                            fontSize="small"
                          />
                        )}
                      </button>
                    </div>
                    {isOtherOpen && (
                      <div className="space-y-1">
                        <button
                          type="button"
                          onClick={() => setShowTaskExport(true)}
                          className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 text-sm text-gray-700"
                        >
                          <div className="flex items-center gap-2">
                            <FileDownloadTwoToneIcon
                              fontSize="small"
                              className="text-gray-500"
                            />
                            <span>タスクデータ出力</span>
                          </div>
                        </button>
                        {roomId !== 0 && (
                          <button
                            type="button"
                            onClick={async () => {
                              await fetchAllTasksIncludingHidden();
                              setShowReminderManagement(true);
                            }}
                            className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 text-sm text-gray-700"
                          >
                            <div className="flex items-center gap-2">
                              <AlarmTwoToneIcon
                                fontSize="small"
                                className="text-gray-500"
                              />
                              <span>リマインダー管理</span>
                            </div>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setShowRecurrenceManagement(true);
                          }}
                          className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 text-sm text-gray-700"
                        >
                          <div className="flex items-center gap-2">
                            <AutorenewIcon
                              fontSize="small"
                              className="text-gray-500"
                            />
                            <span>繰り返し作成一覧</span>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* サイドバー下部の操作ボタン (スクロール範囲の外に配置) */}
                <div className="p-4 mt-auto pt-4 border-t">
                  <button
                    onClick={() => setShowDetailedFilter(true)}
                    className="w-full px-4 py-2 rounded-lg text-sm transition hover:brightness-105 active:scale-[0.98] border-2 border-white"
                    style={{
                      backgroundColor: PaletteColorHex[color],
                      color: "white",
                      backgroundImage:
                        "linear-gradient(to bottom, rgba(255,255,255,0.15), rgba(255,255,255,0))",
                      boxShadow:
                        "inset 0 1px 2px rgba(255,255,255,0.25), 0 3px 5px rgba(0,0,0,0.15)", // 影を抑えた
                    }}
                  >
                    詳細な絞り込み
                  </button>
                  <button
                    onClick={() => setFilters(initialFilterState)}
                    className="w-full text-sm hover:underline mt-2"
                    style={{
                      color: PaletteColorHex[color],
                    }}
                  >
                    絞り込みをリセット
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* メインコンテンツエリア */}
        <div className="flex-1 px-6 pt-4 pb-6 relative flex flex-col min-w-0">
          {/* 浮遊ツールバー（スクロール時に重ねて表示） */}
          <div
            className={`absolute top-2 left-6 z-30 transition-opacity ${
              hasScrolled ? "opacity-95" : "opacity-100"
            }`}
          >
            <div className="flex items-center bg-white/95 backdrop-blur border rounded-md shadow px-1 py-0.5">
              {/* 更新ボタン */}
              <button
                onClick={() => refreshTodos()}
                aria-label="更新"
                title="更新"
                className="p-1.5 rounded hover:bg-gray-100 text-gray-600 transition-colors"
              >
                <RefreshIcon className="w-4 h-4" />
              </button>
              <div className="mx-1 w-px self-stretch bg-gray-200" />
              {/* ビュー切替ボタン - handleViewModeChange を使用 */}
              <div className="flex items-center">
                <button
                  onClick={() => handleViewModeChange("list")}
                  aria-label="リスト表示"
                  title="リスト表示"
                  className={`p-1.5 rounded transition-colors mx-1 ${
                    viewMode === "list"
                      ? "text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  style={
                    viewMode === "list" ? activeViewButtonStyle : undefined
                  }
                >
                  <ListAltIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleViewModeChange("dashboard")}
                  aria-label="ダッシュボード"
                  title="ダッシュボード"
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === "dashboard"
                      ? "text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  style={
                    viewMode === "dashboard"
                      ? activeViewButtonStyle
                      : undefined
                  }
                >
                  <DashboardOutlinedIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleViewModeChange("taskmap")}
                  aria-label="タスクツリー表示"
                  title="タスクツリー表示"
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === "taskmap"
                      ? "text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  style={
                    viewMode === "taskmap" ? activeViewButtonStyle : undefined
                  }
                >
                  <AccountTreeOutlinedIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleViewModeChange("timeline")}
                  aria-label="タイムライン表示"
                  title="タイムライン表示"
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === "timeline"
                      ? "text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  style={
                    viewMode === "timeline" ? activeViewButtonStyle : undefined
                  }
                >
                  <CalendarMonthIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleViewModeChange("board")}
                  aria-label="ボード表示"
                  title="ボード表示"
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === "board"
                      ? "text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  style={
                    viewMode === "board" ? activeViewButtonStyle : undefined
                  }
                >
                  <KebabDiningOutlinedIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="mx-1 w-px self-stretch bg-gray-200" />
              {/* 全体設定ボタン */}
              <button
                onClick={() => setShowGeneralSettingsDialog(true)}
                aria-label="全体設定"
                title="全体設定"
                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 transition-colors"
              >
                <SettingsIcon className="w-5 h-5" />
              </button>
              {/* プロジェクトメモボタン（PC幅狭時のみ表示） */}
              {!isWideScreen && activeListId && activeListId !== "0" && (
                <>
                  <div className="mx-1 w-px self-stretch bg-gray-200" />
                  <div className="relative">
                    <button
                      onClick={() => setIsMemoOverlayOpen(true)}
                      aria-label="プロジェクトメモ"
                      title="プロジェクトメモ"
                      className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 transition-colors"
                    >
                      <ChatIcon className="w-5 h-5" />
                    </button>
                    {hasMemoUnread && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full px-1 py-0.5 leading-none pointer-events-none">
                        NEW
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* メインのタスク表示エリア（リスト/タイムライン/ボード） */}
          <div
            ref={
              viewMode === "list"
                ? listScrollContainerRef
                : viewMode === "table"
                  ? tableScrollContainerRef
                  : viewMode === "taskmap"
                    ? taskMapScrollContainerRef
                    : undefined
            }
            className={`flex-grow relative ${
              viewMode === "list" || viewMode === "table"
                ? "overflow-y-auto" // ListとTableではこのコンテナがスクロール
                : "flex flex-col min-h-0" // 他のビューでは子は自身の内部でスクロール
            } ${viewMode === "list" ? "pt-12" : ""}`}
            onScroll={viewMode === "list" ? handleListScroll : undefined}
            onClick={
              viewMode === "list" || viewMode === "taskmap"
                ? () => setSelectedDetail(null)
                : undefined
            }
          >
            {/* リストビュー */}
            {viewMode === "list" && (
              <>
                {/* タスク一覧 */}
                <div className="space-y-3">
                  {sortedTasks.map((task) => (
                    <div
                      key={task.id}
                      data-task-id={task.id}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TaskItem
                        task={task}
                        isMobile={false}
                        isSelected={
                          isWideScreen && selectedDetail?.parent.id === task.id
                        }
                        selectedSubtaskId={selectedDetail?.child?.id}
                        onSelectDetail={handleSelectDetail}
                        onToggleComplete={toggleTaskComplete}
                        onUpdateStatus={updateTaskStatus}
                        onToggleStar={toggleTaskStar}
                        onToggleNotification={toggleTaskNotification}
                        onToggleSubtask={() => {}}
                        refreshTodos={refreshTodos}
                        color={color}
                        roomId={roomId}
                        honbuId={honbuId}
                        roomStatuses={roomStatuses}
                        reorderSubtasks={reorderSubtasks}
                        onAddSubtask={handleAddSubtask}
                        customColumns={customColumns}
                        customColumnValues={customColumnValues}
                        visibleCustomColumnIds={visibleCustomColumnIds}
                      />
                    </div>
                  ))}
                </div>
                {/* タスクが無い場合の空状態表示 */}
                {sortedTasks.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400">
                    <NightsStayTwoToneIcon
                      style={{ fontSize: 48 }}
                      className="mb-3"
                    />
                    <p className="text-sm leading-relaxed">
                      このプロジェクトにはタスクがありません。
                      <br />
                      ⊕ボタンからタスクを作成してください。
                    </p>
                  </div>
                )}
                {/* 新規タスク作成ボタン（リストビュー下部） */}
                <div
                  className="flex-shrink-0 mt-3 flex items-center gap-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={handleOpenNewTaskDialog}
                    disabled={activeListId === "0"}
                    className={`flex items-center text-sm focus:outline-none ${
                      activeListId === "0"
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-blue-600 hover:underline"
                    }`}
                    style={{ color: PaletteColorHex[color] }}
                    aria-label="新規タスクを作成"
                    title={
                      activeListId === "0"
                        ? "マイリストでは直接作成できません"
                        : "新規タスクを作成"
                    }
                  >
                    <span className="text-base mr-1">＋</span>
                    タスクを新規作成
                  </button>
                  {/* カスタム項目の表示設定（カスタム項目が設定されたプロジェクトのみ表示） */}
                  {activeListId !== "0" && customColumns.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowCustomColumnDialog(true)}
                      className="ml-auto flex items-center gap-1 px-2 py-1 rounded-full text-sm hover:bg-gray-100 focus:outline-none"
                      style={{ color: PaletteColorHex[color] }}
                      aria-label="表示するカスタム項目を設定"
                      title="表示するカスタム項目を設定"
                    >
                      <ExtensionTwoToneIcon fontSize="small" />
                      表示項目の変更
                    </button>
                  )}
                </div>
              </>
            )}
            {/* テーブルビュー */}
            {viewMode === "table" && (
              <TaskTableView
                tasks={sortedTasks}
                roomMembers={roomMembers}
                availableTags={availableTags}
                updateTask={updateTask}
                updateTaskStatus={updateTaskStatus}
                onAddTask={handleAddTaskFromTable}
                onDeleteTask={deleteTask}
                onAddSubtask={addSubtask}
                roomId={roomId}
                honbuId={honbuId}
                refreshTodos={refreshTodos}
                roomStatuses={roomStatuses}
                color={color}
                activeListId={activeListId}
              />
            )}
            {/* タスクツリービュー */}
            {viewMode === "taskmap" && (
              <TaskMapView
                tasks={sortedTasks}
                onSelectDetail={handleSelectDetail}
                onUpdateStatus={updateTaskStatus}
                onAddSubtask={handleAddSubtask}
                reorderSubtasks={reorderSubtasks}
                roomId={roomId}
                honbuId={honbuId}
                refreshTodos={refreshTodos}
                roomStatuses={roomStatuses}
              />
            )}
            {/* タイムラインビュー */}
            {viewMode === "timeline" && (
              <TimelineView
                tasks={sortedTasks}
                roomMembers={timelineUsers}
                onUpdateTask={updateTask}
                onUpdateStatus={updateTaskStatus}
                onDeleteTask={deleteTask}
                availableTags={availableTags}
                onManageTags={() => setShowTagManagement(true)}
                color={color}
                addReminder={addReminder}
                deleteReminder={deleteReminder}
                roomStatuses={roomStatuses}
                honbuId={honbuId}
                roomId={roomId}
                forceReload={forceReload}
                activeListId={activeListId}
                onOpenRecurrenceManagement={handleOpenRecurrenceManagement}
              />
            )}
            {/* ボードビュー */}
            {viewMode === "board" && (
              <TaskBoardView
                tasks={sortedTasks}
                onUpdateStatus={updateTaskStatus}
                fetchAndMergeTasks={fetchAndMergeTasks}
                onAddTask={handleOpenNewTaskDialog}
                onUpdateTask={updateTask}
                onDeleteTask={deleteTask}
                roomMembers={roomMembers}
                availableTags={availableTags}
                onManageTags={() => setShowTagManagement(true)}
                color={color}
                addReminder={addReminder}
                deleteReminder={deleteReminder}
                onShowHiddenColumn={handleShowHiddenColumn}
                roomStatuses={roomStatuses}
                forceReload={forceReload}
                honbuId={honbuId}
                roomId={roomId}
                onOpenRecurrenceManagement={handleOpenRecurrenceManagement}
              />
            )}
            {/* ダッシュボードビュー */}
            {viewMode === "dashboard" && (
              <DashboardView
                tasks={sortedTasks}
                activeListId={activeListId}
                roomId={roomId}
                honbuId={honbuId}
                roomMembers={roomMembers}
                availableTags={availableTags}
                roomStatuses={roomStatuses}
                color={color}
                isDarkMode={isDarkMode}
                addTask={addTask}
                updateTask={updateTask}
                updateTaskStatus={updateTaskStatus}
                onDeleteTask={deleteTask}
                refreshTodos={refreshTodos}
                addReminder={addReminder}
                deleteReminder={deleteReminder}
                forceReload={forceReload}
                onManageTags={() => setShowTagManagement(true)}
                onOpenRecurrenceManagement={handleOpenRecurrenceManagement}
                columnsVersion={customColumnsVersion}
              />
            )}
          </div>

          {/* 右下の新規タスク作成ボタン（フローティング） */}
          <button
            onClick={handleOpenNewTaskDialog}
            disabled={activeListId === "0"}
            className={`absolute bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center z-40 transition border-4 border-white ${
              activeListId === "0"
                ? "cursor-not-allowed text-gray-500"
                : "text-white"
            }`}
            style={
              activeListId === "0"
                ? {
                    // 無効時のスタイル
                    backgroundColor: "#B0B0B0",
                    backgroundImage:
                      "linear-gradient(to bottom, #C0C0C0, #B0B0B0)",
                    boxShadow:
                      "inset 0 1px 1px rgba(255,255,255,0.2), 0 3px 5px rgba(0,0,0,0.15)",
                  }
                : {
                    // 有効時のスタイル
                    backgroundColor: PaletteColorHex[color],
                    backgroundImage:
                      "linear-gradient(to bottom, rgba(255,255,255,0.15), rgba(255,255,255,0))",
                    boxShadow:
                      "inset 0 1px 2px rgba(255,255,255,0.25), 0 3px 5px rgba(0,0,0,0.15)",
                  }
            }
            aria-label="新規タスク"
            title={
              activeListId === "0"
                ? "マイリストでは直接作成できません"
                : "新規タスク"
            }
          >
            <AddIcon className="w-6 h-6" />
          </button>
        </div>

        {/* 詳細パネル（PCのみ） */}
        {isWideScreen && (viewMode === "list" || viewMode === "taskmap") ? (
          // --- PC幅広：静的パネル ---
          <div
            className="flex-shrink-0 bg-white border-l-2 border-gray-200 h-full relative"
            style={{ width: detailPanelWidth }}
          >
            {/* 左辺のドラッグハンドル：つかんで引っ張ると幅を一時的に変更できる */}
            <PanelResizeHandle onPointerDown={handleDetailResizeStart} />
            {selectedDetail ? (
              <TaskDetailPanel
                task={selectedDetail.child ?? selectedDetail.parent}
                isSubtask={!!selectedDetail.child}
                parentForContext={selectedDetail.parent}
                onUpdateTask={updateTask}
                onUpdateStatus={updateTaskStatus}
                onDeleteTask={deleteTask}
                roomMembers={roomMembers}
                availableTags={availableTags}
                onManageTags={() => setShowTagManagement(true)}
                color={color}
                addReminder={addReminder}
                deleteReminder={deleteReminder}
                displayMode="panel"
                roomStatuses={roomStatuses}
                forceReload={forceReload}
                honbuId={honbuId}
                roomId={roomId}
                onOpenRecurrenceManagement={handleOpenRecurrenceManagement}
                onRecurrenceUpdateNeeded={handleRecurrenceUpdateNeeded}
                isMobile={false}
                listId={activeListId ? parseInt(activeListId, 10) : undefined}
                onAddSubtask={handleAddSubtask}
                copyTargetLists={copyTargetLists}
                onCopyToOtherList={handleCopyToOtherList}
                onTaskCopiedInList={handleTaskCopiedInList}
              />
            ) : // ★★★ 変更箇所：タスク未選択時に activeListId があればメモパネルを表示 ★★★
            activeListId && activeListId !== "0" ? (
              <ProjectMemoPanel
                todoListId={parseInt(activeListId, 10)}
                honbuId={honbuId}
                roomId={roomId}
                roomMembers={roomMembers}
                color={color}
                onViewed={() => setHasMemoUnread(false)}
              />
            ) : (
              <div className="p-4 flex items-center justify-center h-full text-gray-500">
                <AnnouncementTwoToneIcon className="w-2 h-2 mr-2" />
                タスクを選択してください
              </div>
            )}
          </div>
        ) : (
          // --- PC幅狭：オーバーレイパネル ---
          <>
            {isDetailOverlayOpen &&
              selectedDetail &&
              (viewMode === "list" || viewMode === "taskmap") && (
                <div
                  className="fixed inset-0 bg-black/50 z-40 flex justify-end"
                  onClick={() => setIsDetailOverlayOpen(false)}
                >
                  <div
                    className="relative h-full bg-white shadow-lg animate-slide-in-right"
                    style={{ width: detailPanelWidth }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PanelResizeHandle
                      onPointerDown={handleDetailResizeStart}
                    />
                    <TaskDetailPanel
                      task={selectedDetail.child ?? selectedDetail.parent}
                      isSubtask={!!selectedDetail.child}
                      parentForContext={selectedDetail.parent}
                      onUpdateTask={updateTask}
                      onUpdateStatus={updateTaskStatus}
                      onDeleteTask={deleteTask}
                      roomMembers={roomMembers}
                      availableTags={availableTags}
                      onManageTags={() => setShowTagManagement(true)}
                      color={color}
                      addReminder={addReminder}
                      deleteReminder={deleteReminder}
                      onClose={() => setIsDetailOverlayOpen(false)}
                      displayMode="overlay"
                      roomStatuses={roomStatuses}
                      forceReload={forceReload}
                      honbuId={honbuId}
                      roomId={roomId}
                      onOpenRecurrenceManagement={
                        handleOpenRecurrenceManagement
                      }
                      onRecurrenceUpdateNeeded={handleRecurrenceUpdateNeeded}
                      isMobile={false}
                      listId={
                        activeListId ? parseInt(activeListId, 10) : undefined
                      }
                      onAddSubtask={handleAddSubtask}
                      copyTargetLists={copyTargetLists}
                      onCopyToOtherList={handleCopyToOtherList}
                      onTaskCopiedInList={handleTaskCopiedInList}
                    />
                  </div>
                </div>
              )}
          </>
        )}

        {/* プロジェクトメモオーバーレイ（PC幅狭） */}
        {isMemoOverlayOpen &&
          !isWideScreen &&
          activeListId &&
          activeListId !== "0" && (
            <div
              className="fixed inset-0 bg-black/50 z-40 flex justify-end"
              onClick={() => setIsMemoOverlayOpen(false)}
            >
              <div
                className="w-96 h-full bg-white shadow-lg animate-slide-in-right"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="h-full flex flex-col">
                  <div className="flex items-center justify-between p-3 border-b">
                    <span className="font-bold text-gray-700">
                      プロジェクトメモ
                    </span>
                    <button
                      onClick={() => setIsMemoOverlayOpen(false)}
                      className="p-1 rounded hover:bg-gray-100 text-gray-500"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <ProjectMemoPanel
                      todoListId={parseInt(activeListId, 10)}
                      honbuId={honbuId}
                      roomId={roomId}
                      roomMembers={roomMembers}
                      color={color}
                      onViewed={() => setHasMemoUnread(false)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* タイムライン・ボード用の詳細モーダル */}
        {modalTask && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex justify-end"
            onClick={() => setModalTask(null)}
          >
            <div
              className="relative h-full bg-white shadow-lg animate-slide-in-right"
              style={{ width: detailPanelWidth }}
              onClick={(e) => e.stopPropagation()}
            >
              <PanelResizeHandle onPointerDown={handleDetailResizeStart} />
              <TaskDetailPanel
                task={modalTask}
                isSubtask={false}
                parentForContext={
                  (modalTask as any).parentId
                    ? (findTaskById(sortedTasks, (modalTask as any).parentId) ??
                      modalTask)
                    : modalTask
                }
                onUpdateTask={updateTask}
                onUpdateStatus={updateTaskStatus}
                onDeleteTask={deleteTask}
                roomMembers={roomMembers}
                availableTags={availableTags}
                onManageTags={() => setShowTagManagement(true)}
                color={color}
                addReminder={addReminder}
                deleteReminder={deleteReminder}
                onClose={() => setModalTask(null)}
                displayMode="overlay"
                roomStatuses={roomStatuses}
                forceReload={forceReload}
                honbuId={honbuId}
                roomId={roomId}
                onOpenRecurrenceManagement={handleOpenRecurrenceManagement}
                onRecurrenceUpdateNeeded={handleRecurrenceUpdateNeeded}
                isMobile={false}
                listId={activeListId ? parseInt(activeListId, 10) : undefined}
                copyTargetLists={copyTargetLists}
                onCopyToOtherList={handleCopyToOtherList}
                onTaskCopiedInList={handleTaskCopiedInList}
              />
            </div>
          </div>
        )}

        {/* 各種ダイアログ（タスク作成・フィルター・詳細・タグ管理・リスト作成・設定） */}
        {showTaskDialog && (
          <TaskDialog
            task={editingTask}
            initialStatus={initialStatusForNewTask}
            onClose={() => {
              setShowTaskDialog(false);
              setInitialStatusForNewTask(undefined);
            }}
            onSave={handleSaveTask}
            availableTags={availableTags}
            roomMembers={roomMembers}
            onManageTags={() => setShowTagManagement(true)}
            color={color}
            roomId={roomId}
            honbuId={honbuId}
            activeListId={activeListId ? parseInt(activeListId, 10) : null}
          />
        )}
        {showDetailedFilter && (
          <FilterDialog
            onClose={() => {
              setShowDetailedFilter(false);
              setEditingMyFilter(null);
            }}
            initialFilters={
              editingMyFilter
                ? JSON.parse(editingMyFilter.filterConditions)
                : filters
            }
            onApplyFilters={(newFilters) => {
              const finalFilters = { ...newFilters }; // 詳細フィルターで担当者が1人でも選択されたら、「自分が担当者」をOFFにする
              if (
                finalFilters.assigneeIds &&
                finalFilters.assigneeIds.length > 0
              ) {
                finalFilters.myTasksOnly = false;
              } // 詳細フィルターで依頼者が1人でも選択されたら、「自分が依頼者」をOFFにする
              if (
                finalFilters.requesterIds &&
                finalFilters.requesterIds.length > 0
              ) {
                finalFilters.myRequestsOnly = false;
              }
              setFilters(finalFilters);
              setShowDetailedFilter(false);
            }}
            availableTags={availableTags}
            roomMembers={roomMembers}
            editingMyFilter={editingMyFilter}
            onSaveMyFilter={handleSaveMyFilter}
            onUpdateMyFilter={handleUpdateMyFilter}
            onDeleteMyFilter={handleDeleteMyFilter}
            color={color}
            roomStatuses={roomStatuses}
            roomId={roomId}
            activeListId={activeListId}
            honbuId={honbuId}
          />
        )}
        {showTagManagement && (
          <TagManagementDialog
            tags={availableTags}
            onClose={() => setShowTagManagement(false)}
            onAddTag={handleAddTag}
            onDeleteTag={handleDeleteTag}
            onUpdateTag={handleUpdateTag}
            color={color}
          />
        )}
        {editingProjectTemplateId !== null && (
          <ProjectTemplateEditorPanel
            projectTemplateId={editingProjectTemplateId}
            onClose={() => setEditingProjectTemplateId(null)}
            roomMembers={roomMembers}
            availableTags={availableTags}
            color={color}
            honbuId={honbuId}
            roomId={roomId}
            // 更新時に何か処理が必要ならここに追加（ListCreateDialog内の再取得など）
            onUpdated={() => {}}
          />
        )}
        {/* リスト作成ダイアログ */}
        {showCreateListDialog && (
          <ListCreateDialog
            honbuId={honbuId}
            onClose={() => {
              setShowCreateListDialog(false);
              setShowCreatePrompt(false);
            }}
            roomId={roomId}
            onAddList={(listData) => {
              addTodoList(listData);
              handleCloseDialogs(true);
            }}
            showCreatePrompt={showCreatePrompt}
            color={color}
            onEditProjectTemplate={(id) => setEditingProjectTemplateId(id)}
          />
        )}
        {/* リスト編集ダイアログ */}
        {showEditListDialog && (
          <ListEditDialog
            key={JSON.stringify(todoLists.map((l) => `${l.id}-${l.name}`))}
            onClose={handleCloseDialogs}
            allLists={todoLists}
            onUpdateListStatus={updateListStatus}
            onDeleteList={deleteList}
            onUpdateListName={handleUpdateListNameAndRefreshUI}
            honbuId={honbuId}
            projectPins={projectPins}
            onTogglePin={toggleProjectPin}
            roomId={roomId}
            color={color}
          />
        )}
        {showGeneralSettingsDialog && (
          <GeneralSettingsDialog
            roomId={roomId}
            onClose={handleCloseGeneralSettings}
            forceReload={forceReload}
            tags={availableTags}
            onAddTag={handleAddTag}
            onDeleteTag={handleDeleteTag}
            onUpdateTag={handleUpdateTag}
            honbuId={honbuId}
            activeListId={activeListId ? parseInt(activeListId, 10) : null}
            todoLists={visibleLists.map((l) => ({ id: l.id, name: l.name }))}
            color={color}
          />
        )}
        {showCustomColumnDialog && (
          <TaskItemCustomColumnDialog
            customColumns={customColumns}
            initialVisibleIds={visibleCustomColumnIds}
            color={color}
            isDarkMode={isDarkMode}
            onClose={() => setShowCustomColumnDialog(false)}
            onSave={handleSaveVisibleCustomColumns}
          />
        )}
        {showTaskExport && (
          <TaskExportDialog
            onClose={() => setShowTaskExport(false)}
            todoLists={visibleLists.map((l) => ({ id: l.id, name: l.name }))}
            roomMembers={roomMembers}
            roomStatuses={roomStatuses}
            availableTags={availableTags}
            color={color}
            roomId={roomId}
            honbuId={honbuId}
          />
        )}
        {showReminderManagement && (
          <ReminderManagementDialog
            tasks={allTasksForDialogs}
            onClose={() => {
              setShowReminderManagement(false);
              setAllTasksForDialogs([]);
            }}
            roomMembers={roomMembers}
            availableTags={availableTags}
            color={color}
            honbuId={honbuId}
            roomId={roomId}
            onUpdateTask={updateTaskForDialog}
            onUpdateStatus={updateTaskStatusForDialog}
            onDeleteTask={deleteTask}
            addReminder={addReminderForDialog}
            deleteReminder={deleteReminderForDialog}
            onManageTags={() => setShowTagManagement(true)}
            roomStatuses={roomStatuses}
            forceReload={forceReloadWithDialogTasks}
            currentTodoListName={
              todoLists.find((l) => l.id === activeListId)?.name ||
              "現在のプロジェクト"
            }
          />
        )}
        {showRecurrenceManagement && (
          <RecurrenceManagementDialog
            onClose={() => {
              setShowRecurrenceManagement(false);
              setRecurrenceInitialTaskId(undefined);
              setRecurrenceInitialRuleId(undefined);
              refreshTodos();
            }}
            roomMembers={roomMembers}
            availableTags={availableTags}
            color={color}
            honbuId={honbuId}
            roomId={roomId}
            currentListId={activeListId ? parseInt(activeListId, 10) : 0}
            onUpdateTask={updateTaskForDialog}
            onDeleteTask={deleteTask}
            addReminder={addReminderForDialog}
            deleteReminder={deleteReminderForDialog}
            onManageTags={() => setShowTagManagement(true)}
            roomStatuses={roomStatuses}
            forceReload={forceReloadWithDialogTasks}
            initialTaskId={recurrenceInitialTaskId}
            initialRuleId={recurrenceInitialRuleId}
          />
        )}
        {recurrenceUpdatePrompt && (
          <RecurrenceUpdatePrompt
            isOriginal={recurrenceUpdatePrompt.isOriginal}
            onConfirm={handleRecurrenceUpdateConfirm}
            onDismiss={handleRecurrenceUpdateDismiss}
            isLoading={recurrenceUpdateLoading}
          />
        )}
      </div>
      {/* /メインエリアラッパー */}
      {/* ヘルプドロワー（メインエリアラッパー外 - transform基点の影響を受けず独立して表示） */}
      <HelpDrawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        paletteColor={color}
        isDark={isDarkMode}
        variant={helpVariant}
        honbuId={honbuId}
        roomId={roomId}
        onTaskMutated={refreshTodos}
      />
    </div>
  );
}

/// ===== モバイル用のメインビューコンポーネント =====
function MobileView({
  honbuId,
  roomId,
  setAppLoading,
  topOffset = 0,
}: {
  honbuId: number;
  roomId: number;
  setAppLoading: (isLoading: boolean) => void;
  topOffset?: number;
}) {
  // 画面幅取得（モバイル判定用、必要に応じて）
  const [filters, setFilters] = useState<FilterState>(initialFilterState);
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // スマホ版：サブタスク表示モード（LocalStorageから読み込み）
  const [showSubtasksOnMobile, setShowSubtasksOnMobile] = useState(() => {
    try {
      const storageKey = `showSubtasksOnMobile_${honbuId}_${roomId}`;
      const saved = localStorage.getItem(storageKey);
      return saved === "true";
    } catch {
      return false;
    }
  });

  // サブタスク表示設定をLocalStorageに保存
  useEffect(() => {
    try {
      const storageKey = `showSubtasksOnMobile_${honbuId}_${roomId}`;
      localStorage.setItem(storageKey, String(showSubtasksOnMobile));
    } catch (error) {
      console.error("Failed to save subtask display setting:", error);
    }
  }, [showSubtasksOnMobile, honbuId, roomId]);

  // URLパラメータ取得からcolorを取得
  const { color, isDarkMode } = useUrlParams();

  // ヘルプドロワーの開閉状態（モバイルは常にオーバーレイ）
  const [helpOpen, setHelpOpen] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "?" &&
        !["INPUT", "TEXTAREA"].includes((e.target as HTMLElement).tagName)
      ) {
        setHelpOpen((prev) => !prev);
      }
    };
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type === "openHelp") setHelpOpen(true);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const [isListMenuOpen, setIsListMenuOpen] = useState(false);

  // プロジェクトピン関連のstate
  const [projectPins, setProjectPins] = useState<Map<number, Date>>(new Map());
  const [arePinsLoading, setArePinsLoading] = useState(true);

  // プロジェクトピンを取得
  useEffect(() => {
    if (honbuId === null) return;
    setArePinsLoading(true);
    (async () => {
      try {
        const response = await fetch(`/api/project-pins?honbuId=${honbuId}`);
        if (response.ok) {
          const pins = await response.json();
          const pinMap = new Map<number, Date>();
          pins.forEach((pin: any) => {
            pinMap.set(pin.todoListId, new Date(pin.pinnedAt));
          });
          setProjectPins(pinMap);
        }
      } catch (error) {
        console.error("Failed to fetch project pins:", error);
      } finally {
        setArePinsLoading(false);
      }
    })();
  }, [honbuId]);

  // ピン留めのトグル
  const toggleProjectPin = async (listId: string) => {
    const todoListId = parseInt(listId, 10);
    const isPinned = projectPins.has(todoListId);

    // 楽観的更新
    setProjectPins((prev) => {
      const newMap = new Map(prev);
      if (isPinned) {
        newMap.delete(todoListId);
      } else {
        newMap.set(todoListId, new Date());
      }
      return newMap;
    });

    try {
      if (isPinned) {
        const response = await fetch(
          `/api/project-pins/${todoListId}?honbuId=${honbuId}`,
          { method: "DELETE" },
        );
        if (!response.ok) throw new Error("Failed to unpin");
      } else {
        const response = await fetch("/api/project-pins", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ honbuId, todoListId }),
        });
        if (!response.ok) throw new Error("Failed to pin");
      }
    } catch (error) {
      console.error("Failed to toggle pin:", error);
      // ロールバック
      setProjectPins((prev) => {
        const newMap = new Map(prev);
        if (isPinned) {
          newMap.set(todoListId, new Date());
        } else {
          newMap.delete(todoListId);
        }
        return newMap;
      });
    }
  };

  // ルームステータス取得
  const [roomStatuses, setRoomStatuses] = useState<RoomStatus[]>([]);
  const fetchRoomStatuses = useCallback(async () => {
    if (roomId === null) return;
    try {
      const response = await fetch(
        `/api/rooms/${roomId}/statuses?honbuId=${honbuId}`,
      );
      if (response.ok) {
        setRoomStatuses(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch room statuses:", error);
    }
  }, [roomId]);

  useEffect(() => {
    fetchRoomStatuses();
  }, [fetchRoomStatuses]);
  // タスク・リスト・操作関数群（useTodosカスタムフック）
  const {
    todoLists,
    setTodoLists,
    activeList,
    activeListId,
    setActiveListId,
    filteredTasks,
    toggleTaskComplete,
    updateTaskStatus,
    toggleTaskStar,
    toggleTaskNotification,
    addTask,
    refreshCustomColumnValues,
    updateTask,
    fetchAndMergeTasks,
    addTodoList,
    updateListStatus,
    deleteList,
    deleteTask,
    addReminder,
    deleteReminder,
    refreshTodos,
    forceReload,
    fetchAllLists,
    isLoading,
    updateListName,
    addSubtask,
    reorderSubtasks,
    updateRecurrenceContents: updateRecurrenceContentsMobile,
    customColumns,
    customColumnValues,
    refreshCustomColumns,
  } = useTodos(roomId, honbuId, filters, roomStatuses);

  // TaskList表示用のカスタム列設定（プロジェクトごと・localStorage）
  const [visibleCustomColumnIds, setVisibleCustomColumnIds] = useState<
    number[]
  >([]);
  const [showCustomColumnDialog, setShowCustomColumnDialog] = useState(false);

  useEffect(() => {
    setVisibleCustomColumnIds(
      loadVisibleCustomColumnIds(honbuId, roomId, activeListId),
    );
  }, [honbuId, roomId, activeListId]);

  const handleSaveVisibleCustomColumns = useCallback(
    (ids: number[]) => {
      saveVisibleCustomColumnIds(honbuId, roomId, activeListId, ids);
      setVisibleCustomColumnIds(ids);
    },
    [honbuId, roomId, activeListId],
  );

  // 自動作成設定の同期プロンプト（モバイル）
  const [recurrenceUpdatePromptMobile, setRecurrenceUpdatePromptMobile] =
    useState<{
      ruleId: number;
      task: Task;
      isOriginal: boolean;
      customColumnValues?: CustomColumnValue[];
    } | null>(null);
  const [recurrenceUpdateLoadingMobile, setRecurrenceUpdateLoadingMobile] =
    useState(false);

  const handleRecurrenceUpdateNeededMobile = useCallback(
    (info: {
      ruleId: number;
      task: Task;
      isOriginal: boolean;
      customColumnValues?: CustomColumnValue[];
    }) => {
      setRecurrenceUpdatePromptMobile(info);
    },
    [],
  );

  const handleRecurrenceUpdateConfirmMobile = useCallback(async () => {
    if (!recurrenceUpdatePromptMobile) return;
    setRecurrenceUpdateLoadingMobile(true);
    const { ruleId, task, customColumnValues } = recurrenceUpdatePromptMobile;
    const contents = JSON.stringify({
      id: task.id, // サブタスクのカスタム項目値を DB から補完するためのひも付け用
      title: task.title,
      memo: task.memo || "",
      priority: task.priority,
      notification: task.notification,
      assignees: (task.assignees || []).map((a) => ({
        id: parseInt(a.id),
        name: a.name,
      })),
      requesters: (task.requesters || []).map((r) => ({
        id: parseInt(r.id),
        name: r.name,
      })),
      tags: (task.tags || []).map((t) => ({
        id: t.id,
        name: t.name,
        color: t.color,
      })),
      childTasks: buildChildTasksForContents(task.childTasks || []),
      customColumnValues: (customColumnValues || [])
        .filter((v) => v.value != null && v.value !== "")
        .map((v) => ({ customColumnId: v.customColumnId, value: v.value })),
    });
    await updateRecurrenceContentsMobile(ruleId, contents);
    setRecurrenceUpdateLoadingMobile(false);
    setRecurrenceUpdatePromptMobile(null);
  }, [recurrenceUpdatePromptMobile, updateRecurrenceContentsMobile]);

  // タスクを別プロジェクトにコピー後、そのプロジェクトに切り替える
  const handleCopyToOtherList = useCallback(
    (targetListId: number, newTaskId?: string) => {
      setDetailTask(null);
      setActiveListId(String(targetListId));
      // 切り替え先プロジェクトでコピーしたタスクを選択・表示する
      if (newTaskId) pendingSelectCopiedTaskIdRef.current = newTaskId;
      forceReload();
    },
    [setActiveListId, forceReload],
  );

  // 同一プロジェクト内コピー完了後、作成されたタスクを詳細パネルに選択・表示する
  const handleTaskCopiedInList = useCallback(
    (newTaskId: string) => {
      pendingSelectCopiedTaskIdRef.current = newTaskId;
      forceReload();
    },
    [forceReload],
  );

  // 表示するリスト（ピン留めでソート）
  const visibleLists = useMemo(() => {
    const lists =
      roomId === 0 ? todoLists : todoLists.filter((l) => l.status === "Active");

    // ピン留めされているものを上に、新しいピン留め順（pinnedAtが新しい順）でソート
    return [...lists].sort((a, b) => {
      const aPinned = projectPins.has(parseInt(a.id, 10));
      const bPinned = projectPins.has(parseInt(b.id, 10));

      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;

      if (aPinned && bPinned) {
        const aDate = projectPins.get(parseInt(a.id, 10))!;
        const bDate = projectPins.get(parseInt(b.id, 10))!;
        return bDate.getTime() - aDate.getTime(); // 新しい順
      }

      return 0; // 両方ピンなしの場合は元の順序を維持
    });
  }, [todoLists, roomId, projectPins]);

  // プロジェクト一覧のバッジ用: リストごとの期限切れ・今日期限の件数（サブタスク含む）
  const deadlineCountsByList = useMemo(() => {
    const today = todayYmd();
    const map = new Map<string, { overdue: number; today: number }>();
    for (const list of todoLists) {
      map.set(list.id, countDeadlineStates(list.tasks ?? [], today));
    }
    return map;
  }, [todoLists]);

  // ピンとリストの読み込みが完了したら、一番上のプロジェクトを選択
  useEffect(() => {
    // ピンとリストの読み込みが完了していない場合はスキップ
    if (arePinsLoading || isLoading) return;

    // 既にactiveListIdが有効な値で設定されている場合はスキップ
    if (
      activeListId &&
      activeListId !== "0" &&
      visibleLists.some((l) => l.id === activeListId)
    ) {
      return;
    }

    // visibleListsの一番上を選択（ピン留めでソート済みなので、最新のピン留めが一番上）
    if (visibleLists.length > 0) {
      setActiveListId(visibleLists[0].id);
    }
  }, [arePinsLoading, isLoading, visibleLists, activeListId, setActiveListId]);

  // プロジェクトメモ未読チェック（アクティブリスト変更時）
  useEffect(() => {
    if (!activeListId || activeListId === "0") {
      setHasMemoUnread(false);
      return;
    }
    fetch(
      `/api/todolists/${activeListId}/memos/has-unread?honbuId=${honbuId}&roomId=${roomId}`,
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) setHasMemoUnread(data.hasUnread);
      })
      .catch(console.error);
  }, [activeListId, honbuId, roomId]);

  const [editingProjectTemplateId, setEditingProjectTemplateId] = useState<
    number | null
  >(null);

  // タグ / メンバー
  const [roomMembers, setRoomMembers] = useState<User[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [areTagsLoading, setAreTagsLoading] = useState(true);
  const [areMembersLoading, setAreMembersLoading] = useState(true);

  // ダイアログ類
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [initialStatusForNewTask, setInitialStatusForNewTask] = useState<
    Task["status"] | undefined
  >();
  const [detailTask, setDetailTask] = useState<{
    task: Task;
    parent: Task;
  } | null>(null);
  // コピーで作成されたタスクIDを保持（コピー後に詳細パネルへ選択・表示するため）
  const pendingSelectCopiedTaskIdRef = useRef<string | null>(null);
  const [showTagManagement, setShowTagManagement] = useState(false);
  const [showCreatePrompt, setShowCreatePrompt] = useState(false);
  const [showCreateListDialog, setShowCreateListDialog] = useState(false);
  const [showEditListDialog, setShowEditListDialog] = useState(false);
  const [showGeneralSettingsDialog, setShowGeneralSettingsDialog] =
    useState(false);

  // プロジェクトメモダイアログ（MobileView用）
  const [isMemoDialogOpen, setIsMemoDialogOpen] = useState(false);
  // プロジェクトメモ未読バッジ（MobileView用）
  const [hasMemoUnread, setHasMemoUnread] = useState(false);

  // 繰り返し作成管理ダイアログの状態（MobileView用）
  const [showRecurrenceManagementMobile, setShowRecurrenceManagementMobile] =
    useState(false);
  const [recurrenceInitialTaskIdMobile, setRecurrenceInitialTaskIdMobile] =
    useState<string | undefined>(undefined);
  const [recurrenceInitialRuleIdMobile, setRecurrenceInitialRuleIdMobile] =
    useState<number | undefined>(undefined);
  const [allTasksForDialogsMobile, setAllTasksForDialogsMobile] = useState<
    Task[]
  >([]);

  // リマインダー管理ダイアログの状態（MobileView用）
  const [showReminderManagementMobile, setShowReminderManagementMobile] =
    useState(false);

  // ManageHistory メニューの表示状態（MobileView用）
  const [showManageHistoryMenu, setShowManageHistoryMenu] = useState(false);

  // ソートメニューの表示状態（MobileView用）
  const [showSortMenu, setShowSortMenu] = useState(false);

  // 検索バーの表示状態（MobileView用）
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showMobileSearch) {
      setTimeout(() => mobileSearchInputRef.current?.focus(), 50);
    }
  }, [showMobileSearch]);

  const handleToggleMobileSearch = () => {
    if (showMobileSearch) {
      setShowMobileSearch(false);
      setFilters((f) => ({ ...f, searchKeyword: "" }));
    } else {
      setShowMobileSearch(true);
    }
  };

  const handleCloseGeneralSettings = (settingsChanged: boolean) => {
    setShowGeneralSettingsDialog(false);
    if (settingsChanged) {
      // カスタム列の追加・変更をリストビューへ即時反映する
      refreshCustomColumns();
      fetchRoomStatuses().then(() => {
        refreshTodos();
      });
    }
  };

  const handleOpenEditListDialog = async () => {
    await fetchAllLists();
    setShowEditListDialog(true);
  };

  const handleCloseDialogs = (settingsChanged: boolean) => {
    setShowCreateListDialog(false);
    setShowEditListDialog(false);
    setShowCreatePrompt(false);
    if (settingsChanged) {
      refreshTodos();
    }
  };

  // 繰り返し作成管理用に全タスクを取得（MobileView用）
  const fetchAllTasksIncludingHiddenMobile = useCallback(async () => {
    try {
      // 正しいエンドポイントを使用して全てのタスクを取得
      const response = await fetch(
        `/api/todolists?roomId=${roomId}&honbuId=${honbuId}&includeHidden=true`,
      );
      if (response.ok) {
        const data = await response.json();
        // 該当するリストを見つけてタスクを設定
        const currentList = data.lists?.find(
          (list: any) => list.todoListId === activeListId,
        );
        setAllTasksForDialogsMobile(currentList?.tasks || []);
      } else {
        setAllTasksForDialogsMobile([]);
      }
    } catch (error) {
      console.error("Failed to fetch all tasks:", error);
      setAllTasksForDialogsMobile([]);
    }
  }, [activeListId, honbuId, roomId]);

  // 繰り返し作成管理ダイアログを開く（MobileView用）
  const handleOpenRecurrenceManagement = useCallback(
    async (selectedTaskId?: string, selectedRuleId?: number) => {
      setRecurrenceInitialTaskIdMobile(selectedTaskId);
      setRecurrenceInitialRuleIdMobile(selectedRuleId);
      setShowRecurrenceManagementMobile(true);
    },
    [],
  );

  // ===== Pull To Refresh (ヘッダー固定・ヘッダー下のギャップのみ伸びる) =====
  const MAX_PULL = 90;
  const TRIGGER_PULL = 60;
  const INDICATOR_MAX = 52;
  const TOP_CONTENT_GAP = 16;
  const FALLBACK_HEADER_HEIGHT = 56;

  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const touchStartY = useRef<number | null>(null);
  const pullingRef = useRef(false);

  // ヘッダー高さ計測
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const measure = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.getBoundingClientRect().height);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);

  // 計測結果が 0 の間はフォールバック値を使用
  const effectiveHeaderHeight =
    headerHeight > 0 ? headerHeight : FALLBACK_HEADER_HEIGHT;

  const beginTouch = (e: React.TouchEvent) => {
    if (refreshing) return;
    if (!scrollRef.current) return;
    if (scrollRef.current.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      pullingRef.current = true;
    }
  };
  const moveTouch = (e: React.TouchEvent) => {
    if (!pullingRef.current || refreshing) return;
    if (touchStartY.current == null) return;
    if (!scrollRef.current) return;
    if (scrollRef.current.scrollTop > 0) {
      // 途中でスクロールされたらキャンセル
      pullingRef.current = false;
      setPullDistance(0);
      return;
    }
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta > 0) {
      const dist = Math.min(MAX_PULL, delta * 0.55);
      setPullDistance(dist);
    } else if (delta < 0) {
      setPullDistance(0);
    }
  };
  const endTouch = () => {
    if (!pullingRef.current) return;
    pullingRef.current = false;
    if (pullDistance >= TRIGGER_PULL && !refreshing) {
      triggerRefresh();
    } else {
      setPullDistance(0);
    }
    touchStartY.current = null;
  };
  const triggerRefresh = async () => {
    setRefreshing(true);
    setPullDistance(INDICATOR_MAX);
    try {
      await Promise.resolve(refreshTodos());
    } finally {
      setTimeout(() => {
        setRefreshing(false);
        setPullDistance(0);
      }, 400);
    }
  };

  // ===== データ取得 =====
  useEffect(() => {
    if (
      !isLoading &&
      !areTagsLoading &&
      !areMembersLoading &&
      !arePinsLoading
    ) {
      setAppLoading(false);
    }
  }, [
    isLoading,
    areTagsLoading,
    areMembersLoading,
    arePinsLoading,
    setAppLoading,
  ]);

  const handleAddTag = async (name: string, color: string) => {
    if (!name.trim()) return;
    setAreTagsLoading(true);
    try {
      const res = await fetch("/api/tags", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, color, roomId, createdHonbuId: honbuId }),
      });
      if (!res.ok) throw new Error("create tag failed");
      const r = await fetch(`/api/tags?roomId=${roomId}&honbuId=${honbuId}`);
      if (r.ok) {
        const data = await r.json();
        setAvailableTags(
          data.map((t: any) => ({
            id: Number(t.tagId),
            name: t.name,
            color: t.color ?? "#cccccc",
          })),
        );
      } else {
        refreshTodos();
      }
    } catch (e) {
      console.error("handleAddTag failed:", e);
    } finally {
      setAreTagsLoading(false);
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    setAreTagsLoading(true);
    try {
      const res = await fetch(`/api/tags/${tagId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("delete tag failed");

      // 削除成功したら最新のタグ一覧を取得して反映
      const r = await fetch(`/api/tags?roomId=${roomId}&honbuId=${honbuId}`);
      if (r.ok) {
        const data = await r.json();
        setAvailableTags(
          data.map((t: any) => ({
            id: Number(t.tagId),
            name: t.name,
            color: t.color ?? "#cccccc",
          })),
        );
      } else {
        refreshTodos();
      }
    } catch (e) {
      console.error("handleDeleteTag failed:", e);
    } finally {
      setAreTagsLoading(false);
    }
  };

  // タグ更新
  const handleUpdateTag = async (
    tagId: number,
    name: string,
    color: string,
  ) => {
    setAreTagsLoading(true);
    try {
      const res = await fetch(`/api/tags/${tagId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, color }),
      });
      if (!res.ok) throw new Error("update tag failed");
      const r = await fetch(`/api/tags?roomId=${roomId}&honbuId=${honbuId}`);
      if (r.ok) {
        const data = await r.json();
        setAvailableTags(
          data.map((t: any) => ({
            id: Number(t.tagId),
            name: t.name,
            color: t.color ?? "#cccccc",
          })),
        );
      } else {
        refreshTodos();
      }
    } catch (e) {
      console.error("handleUpdateTag failed:", e);
    } finally {
      setAreTagsLoading(false);
    }
  };

  useEffect(() => {
    setAvailableTags([]);
    setAreTagsLoading(true);
    if (roomId == null) return;
    (async () => {
      try {
        const r = await fetch(`/api/tags?roomId=${roomId}&honbuId=${honbuId}`);
        if (!r.ok) throw new Error();
        const data = await r.json();
        setAvailableTags(
          data.map((t: any) => ({
            id: Number(t.tagId),
            name: t.name,
            color: t.color ?? "#cccccc",
          })),
        );
      } catch {
        setAvailableTags([]);
      } finally {
        setAreTagsLoading(false);
      }
    })();
  }, [roomId, honbuId]);

  // MobileView でもフォールバックマージ
  useEffect(() => {
    if (areTagsLoading) return;
    if (roomId === 0) return;
    const taskTags = new Map<number, Tag>();
    todoLists.forEach((l) =>
      l.tasks.forEach((t) =>
        (t.tags || []).forEach((tag) => {
          const idNum = Number(tag.id);
          if (!taskTags.has(idNum)) {
            taskTags.set(idNum, {
              id: idNum,
              name: tag.name,
              color: tag.color ?? "#cccccc",
            });
          }
        }),
      ),
    );
    if (taskTags.size === 0) return;
    setAvailableTags((prev) => {
      const exist = new Set(prev.map((p) => p.id));
      const merged = [...prev];
      taskTags.forEach((tag) => {
        if (!exist.has(tag.id)) merged.push(tag);
      });
      return merged;
    });
  }, [todoLists, areTagsLoading, roomId]);

  useEffect(() => {
    if (roomId == null) return;
    setRoomMembers([]);
    setAreMembersLoading(true);

    const ac = new AbortController();
    const currentRoom = roomId;
    (async () => {
      try {
        const res = await fetch(`/api/rooms/${currentRoom}/members`, {
          signal: ac.signal,
        });
        if (!res.ok) throw new Error("members fetch failed");
        const data = await res.json();
        if (ac.signal.aborted || currentRoom !== roomId) return;

        setRoomMembers(
          data.map((m: any) => ({
            id: String(m.honbuId),
            name: m.name,
          })),
        );
      } catch (e) {
        if (!ac.signal.aborted) {
          console.error(e);
          setRoomMembers([]);
        }
      } finally {
        if (!ac.signal.aborted) setAreMembersLoading(false);
      }
    })();
    return () => ac.abort();
  }, [roomId, honbuId]);

  // ===== タスク保存処理 =====
  const memberNameToIdMap = new Map(roomMembers.map((m) => [m.name, m.id]));
  const getMemberIdsFromNames = (names: string[]) =>
    (names || [])
      .map((n) => parseInt(memberNameToIdMap.get(n) || "0", 10))
      .filter((id) => id !== 0);

  const getTagIdsFromNames = (names: string[]) =>
    (names || [])
      .map((n) => availableTags.find((t) => t.name === n)?.id || 0)
      .filter((id) => id !== 0);

  const handleSaveTask = async (formData: any, isBulk: boolean) => {
    if (!editingTask) {
      const taskData: TaskCreateDto = {
        title: formData.title,
        todoListId: activeListId ? parseInt(activeListId, 10) : 0,
        tagIds: getTagIdsFromNames(formData.tags),
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        memo: formData.memo || undefined,
        notification: formData.notification,
        priority: formData.priority,
        createdHonbuId: honbuId,
        roomId: roomId,
        type: formData.type,
        status: formData.status,
        // F017: テンプレート由来の既存添付ファイルへリンクする
        fileIds: formData.fileIds || [],
      };
      if (formData.type === "single") {
        taskData.assigneeHonbuIds = getMemberIdsFromNames(formData.assignees);
        taskData.requesterHonbuIds = getMemberIdsFromNames(formData.requesters);
      } else {
        taskData.childTasks = formData.childTasks.map((c: any) => ({
          title: c.title,
          assigneeHonbuIds: getMemberIdsFromNames(c.assignees),
          requesterHonbuIds: getMemberIdsFromNames(c.requesters),
          startDate: c.startDate || undefined,
          endDate: c.endDate || undefined,
          memo: c.memo || undefined,
          customColumnValues: (c.customColumnValues ?? []).filter(
            (v: { value: string }) => v.value !== "",
          ),
        }));
      }
      const newTaskId = await addTask(taskData, formData.assignees || []);
      const ccv = formData.customColumnValues as
        | { customColumnId: number; value: string }[]
        | undefined;
      if (newTaskId && ccv && ccv.length > 0) {
        await Promise.all(
          ccv.map((c) =>
            fetch(
              `/api/tasks/${newTaskId}/custom-column-values/${c.customColumnId}`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ value: c.value }),
              },
            ).catch(() => {}),
          ),
        );
        refreshCustomColumnValues();
      }
      // F017: ダイアログで選択されたファイルをタスク作成後にアップロードする
      if (newTaskId && formData.pendingFiles && formData.pendingFiles.length > 0) {
        try {
          for (const f of formData.pendingFiles as File[]) {
            await uploadTaskFile(newTaskId, honbuId, f);
          }
        } catch (e) {
          console.error("Failed to upload attachments:", e);
        }
        await forceReload();
      }
    }
    setShowTaskDialog(false);
    setEditingTask(null);
  };

  const handleOpenNewTaskDialog = (
    initialStatusOrEvent?: Task["status"] | React.MouseEvent,
  ) => {
    const canAddTask = activeListId && activeListId !== "0";

    if (canAddTask) {
      const initialStatus =
        typeof initialStatusOrEvent === "string"
          ? (initialStatusOrEvent as Task["status"])
          : undefined;
      setEditingTask(null);
      setInitialStatusForNewTask(initialStatus);
      setShowTaskDialog(true);
    } else {
      setShowCreatePrompt(true);
      setShowCreateListDialog(true);
    }
  };

  // const handleCreateList = (name: string) => {
  //   const listData: TodoListCreateDto = {
  //     name,
  //     roomId,
  //     createdHonbuId: honbuId,
  //   };
  //   addTodoList(listData);
  // };

  const handleUpdateListNameAndRefreshUI = async (
    listId: string,
    name: string,
  ): Promise<boolean> => {
    const previousTodoLists = [...todoLists];
    setTodoLists((currentLists) =>
      currentLists.map((list) =>
        list.id === listId ? { ...list, name: name.trim() } : list,
      ),
    );
    const success = await updateListName(listId, name);
    if (!success) {
      setTodoLists(previousTodoLists);
    }
    return success;
  };

  // ===== ソート済みタスクリスト =====
  const sortedTasks = useMemo(() => {
    const statusOrder: Record<Task["status"], number> = {
      対応中: 1,
      未対応: 2,
      待機: 3,
      完了: 4,
      非表示: 5,
    };
    const priorityOrder: Record<Task["priority"], number> = {
      高: 1,
      中: 2,
      低: 3,
      なし: 4,
    };
    return [...filteredTasks].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "status")
        cmp = (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
      else if (sortKey === "dueDate") {
        const da = a.startDate ? new Date(a.startDate).getTime() : Infinity;
        const db = b.startDate ? new Date(b.startDate).getTime() : Infinity;
        cmp = da - db;
      } else if (sortKey === "priority") {
        cmp =
          (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
      } else {
        cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [filteredTasks, sortKey, sortDirection]);

  useEffect(() => {
    if (!detailTask) return;
    const freshParent = sortedTasks.find((t) => t.id === detailTask.parent.id);
    if (!freshParent) return;
    if (freshParent !== detailTask.parent) {
      setDetailTask((prev) => (prev ? { ...prev, parent: freshParent } : null));
    }
  }, [sortedTasks, detailTask]);

  // コピーで作成されたタスクをデータ再取得後に詳細パネルへ選択・表示する
  // 上の reconciliation 効果より後に実行することで新タスクの選択が優先される
  useEffect(() => {
    const id = pendingSelectCopiedTaskIdRef.current;
    if (!id) return;
    const task = sortedTasks.find((t) => t.id === id);
    if (!task) return;
    setDetailTask({ task, parent: task });
    pendingSelectCopiedTaskIdRef.current = null;
  }, [sortedTasks]);

  // ===== レイアウト =====
  const indicatorHeight = refreshing
    ? INDICATOR_MAX
    : Math.min(pullDistance, INDICATOR_MAX);

  return (
    <div className="flex flex-col h-full bg-gray-100 min-h-0 overflow-hidden">
      {/* 固定ヘッダー */}
      <div
        ref={headerRef}
        className="fixed left-0 right-0 z-50 bg-white shadow-md border-b"
        style={{ top: topOffset }}
      >
        <div className="relative pt-[env(safe-area-inset-top,0px)] px-2 pb-2 flex items-center justify-between">
          {/* --- プルダウンボタン --- */}
          <div className="flex-1 min-w-0">
            <button
              onClick={() => setIsListMenuOpen(!isListMenuOpen)}
              className="flex items-center gap-1 text-lg font-bold p-2 rounded-md hover:bg-gray-100"
            >
              <span className="truncate">
                {activeList?.name || "プロジェクトを選択"}
              </span>
              <ExpandMoreIcon
                className={`transition-transform ${
                  isListMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>

          {/* --- 右側に固定するボタン部分 --- */}
          <div className="flex-shrink-0 flex items-center bg-white pl-2">
            {activeListId && activeListId !== "0" && (
              <div className="relative">
                <button
                  onClick={() => setIsMemoDialogOpen(true)}
                  className="p-2 text-gray-500 hover:text-gray-800"
                  title="プロジェクトメモ"
                >
                  <ChatIcon className="w-5 h-5" />
                </button>
                {hasMemoUnread && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full px-1 py-0.5 leading-none pointer-events-none">
                    NEW
                  </span>
                )}
              </div>
            )}
            <button
              onClick={() => setShowCreateListDialog(true)}
              className="p-2 text-gray-500 hover:text-gray-800"
              title="リストの新規作成"
            >
              <LibraryAddTwoToneIcon className="w-5 h-5" />
            </button>
            <button
              onClick={handleOpenEditListDialog}
              className="p-2 text-gray-500 hover:text-gray-800"
              title="リストの編集"
            >
              <DriveFileRenameOutlineTwoToneIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowGeneralSettingsDialog(true)}
              className="p-2 text-gray-500 hover:text-gray-800"
              title="全体設定"
            >
              <SettingsIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* リストメニュー */}
      {isListMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setIsListMenuOpen(false)}
          ></div>
          <div
            className="absolute z-40 bg-white rounded-b-lg shadow-lg w-full"
            style={{ top: effectiveHeaderHeight }}
          >
            {visibleLists.map((list) => {
              const isPinned = projectPins.has(parseInt(list.id, 10));
              return (
                <div
                  key={list.id}
                  className={`w-full flex items-center ${
                    activeListId === list.id ? "text-white" : "text-gray-800"
                  }`}
                  style={
                    activeListId === list.id
                      ? { backgroundColor: PaletteColorHex[color] }
                      : {}
                  }
                >
                  <button
                    onClick={() => {
                      setActiveListId(list.id);
                      setIsListMenuOpen(false);
                    }}
                    className="flex-1 min-w-0 text-left px-4 py-3 text-md font-medium flex items-center"
                  >
                    <span className="truncate">{list.name}</span>
                    <DeadlineCountBadges
                      overdue={deadlineCountsByList.get(list.id)?.overdue ?? 0}
                      today={deadlineCountsByList.get(list.id)?.today ?? 0}
                    />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleProjectPin(list.id);
                    }}
                    className={`p-3 ${
                      activeListId === list.id
                        ? "text-white hover:text-gray-200"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                    title={isPinned ? "ピン留めを解除" : "ピン留め"}
                  >
                    {isPinned ? (
                      <PushPinIcon fontSize="small" />
                    ) : (
                      <PushPinOutlinedIcon fontSize="small" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* プルリフレッシュインジケータ（ヘッダー直下・固定） */}
      <div
        className={`fixed left-0 right-0 z-60 flex items-center justify-center overflow-hidden ${
          indicatorHeight > 0 ? "bg-white" : ""
        }`}
        style={{
          top: effectiveHeaderHeight,
          height: indicatorHeight,
          transition:
            pullingRef.current || refreshing
              ? "none"
              : "height 180ms ease, background-color 180ms",
        }}
      >
        {(indicatorHeight > 0 || refreshing) && (
          <div className="flex items-center gap-2 text-[12px] text-gray-600">
            {refreshing ? (
              <>
                <SettingsIcon className="w-6 h-6 animate-spin text-blue-500" />
                更新中...
              </>
            ) : pullDistance >= TRIGGER_PULL ? (
              <span>離して更新</span>
            ) : (
              <span>下に引っ張って更新</span>
            )}
          </div>
        )}
      </div>

      {/* モバイル検索バー */}
      {showMobileSearch && (
        <div className="fixed bottom-[4rem] left-0 right-0 z-50 px-3 py-2">
          <div className="relative flex items-center">
            <SearchIcon
              className="absolute left-2.5 text-gray-400 pointer-events-none"
              fontSize="small"
            />
            <input
              ref={mobileSearchInputRef}
              type="text"
              value={filters.searchKeyword}
              onChange={(e) =>
                setFilters((f) => ({ ...f, searchKeyword: e.target.value }))
              }
              placeholder="メインタスク名で絞り込み"
              className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-gray-400 bg-gray-50"
            />
            <button
              type="button"
              onClick={() => {
                if (filters.searchKeyword) {
                  setFilters((f) => ({ ...f, searchKeyword: "" }));
                } else {
                  setShowMobileSearch(false);
                }
              }}
              className="absolute right-2 p-0.5 rounded-full hover:bg-gray-100 text-gray-500"
              aria-label={filters.searchKeyword ? "検索をクリア" : "検索を閉じる"}
            >
              <ClearIcon fontSize="small" />
            </button>
          </div>
        </div>
      )}

      {/* 固定フッター */}
      <footer className="fixed bottom-0 left-0 right-0 z-50 h-14 bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.12)] border-t border-gray-200 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilterDialog(true)}
              className="text-gray-600 p-1"
              aria-label="絞り込み"
              title="絞り込み"
            >
              <FilterAltTwoToneIcon className="w-6 h-6" />
            </button>
            <button
              onClick={handleToggleMobileSearch}
              className="p-1"
              style={
                showMobileSearch || filters.searchKeyword
                  ? { color: PaletteColorHex[color] }
                  : { color: "#4B5563" }
              }
              aria-label="検索"
              title="検索"
            >
              <SearchIcon className="w-6 h-6" />
            </button>
            <button
              onClick={() => setShowSubtasksOnMobile(!showSubtasksOnMobile)}
              className={`p-1 ${
                showSubtasksOnMobile ? "text-gray-700" : "text-gray-400"
              }`}
              aria-label="サブタスク表示切り替え"
              title="サブタスク表示切り替え"
            >
              {showSubtasksOnMobile ? (
                <ViewDayTwoToneIcon className="w-6 h-6" />
              ) : (
                <ViewHeadlineTwoToneIcon className="w-6 h-6" />
              )}
            </button>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowManageHistoryMenu(!showManageHistoryMenu)}
              className="text-gray-600 p-1 relative"
              aria-label="リマインダー・自動作成管理"
              title="リマインダー・自動作成管理"
            >
              <ManageHistoryTwoToneIcon className="w-6 h-6" />
            </button>
            <button
              onClick={() => setShowSortMenu(true)}
              className="flex items-center gap-1 text-gray-600 p-1"
              aria-label="並べ替え"
              title="並べ替え"
            >
              <SortIcon className="w-6 h-6" />
              <span className="text-[11px] leading-tight">
                {sortKey === "createdAt"
                  ? "作成日"
                  : sortKey === "status"
                    ? "ステータス"
                    : sortKey === "dueDate"
                      ? "期限"
                      : "重要度"}
              </span>
            </button>
          </div>
        </div>
        <button
          onClick={handleOpenNewTaskDialog}
          disabled={activeListId === "0"}
          className={`absolute -top-7 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full flex items-center justify-center z-40 transition border-4 border-white ${
            activeListId === "0"
              ? "cursor-not-allowed text-gray-500"
              : "text-white"
          }`}
          style={
            activeListId === "0"
              ? {
                  // 無効時のスタイル
                  backgroundColor: "#B0B0B0",
                  backgroundImage:
                    "linear-gradient(to bottom, #C0C0C0, #B0B0B0)",
                  boxShadow:
                    "inset 0 1px 1px rgba(255,255,255,0.2), 0 3px 5px rgba(0,0,0,0.15)", // 影を抑えた
                }
              : {
                  // 有効時のスタイル
                  backgroundColor: PaletteColorHex[color],
                  backgroundImage:
                    "linear-gradient(to bottom, rgba(255,255,255,0.15), rgba(255,255,255,0))",
                  boxShadow:
                    "inset 0 1px 2px rgba(255,255,255,0.25), 0 3px 5px rgba(0,0,0,0.15)", // 影を抑えた
                }
          }
          aria-label="新規タスク"
          title={
            activeListId === "0"
              ? "マイリストでは直接作成できません"
              : "新規タスク"
          }
        >
          <AddIcon className="w-8 h-8" />
        </button>
      </footer>

      {/* ソートメニュー */}
      {showSortMenu && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-50"
            onClick={() => setShowSortMenu(false)}
          />
          <div className="fixed bottom-14 right-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[180px] pb-[env(safe-area-inset-bottom)]">
            {(
              [
                { key: "createdAt", dir: "asc", label: "作成日（古い順）" },
                { key: "createdAt", dir: "desc", label: "作成日（新しい順）" },
                { key: "status", dir: "asc", label: "ステータス順" },
                { key: "dueDate", dir: "asc", label: "期限（古い順）" },
                { key: "dueDate", dir: "desc", label: "期限（新しい順）" },
                { key: "priority", dir: "asc", label: "重要度順" },
              ] as { key: SortKey; dir: SortDirection; label: string }[]
            ).map((option) => {
              const isActive =
                sortKey === option.key && sortDirection === option.dir;
              return (
                <button
                  key={`${option.key}-${option.dir}`}
                  onClick={() => {
                    setSortKey(option.key);
                    setSortDirection(option.dir);
                    setShowSortMenu(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 text-left ${
                    isActive ? "font-semibold" : "text-gray-700"
                  }`}
                  style={isActive ? { color: PaletteColorHex[color] } : {}}
                >
                  <span className="text-sm">{option.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* ManageHistory メニュー */}
      {showManageHistoryMenu && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-50"
            onClick={() => setShowManageHistoryMenu(false)}
          />
          <div className="fixed bottom-14 left-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[200px] pb-[env(safe-area-inset-bottom)]">
            {roomId !== 0 && (
              <button
                onClick={async () => {
                  setShowManageHistoryMenu(false);
                  await fetchAllTasksIncludingHiddenMobile();
                  setShowReminderManagementMobile(true);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 text-left"
              >
                <AlarmTwoToneIcon className="text-gray-500" fontSize="small" />
                <span className="text-sm text-gray-700">リマインダー一覧</span>
              </button>
            )}
            <button
              onClick={() => {
                setShowManageHistoryMenu(false);
                setShowRecurrenceManagementMobile(true);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 text-left"
            >
              <AutorenewIcon className="text-gray-500" fontSize="small" />
              <span className="text-sm text-gray-700">自動作成管理</span>
            </button>
          </div>
        </>
      )}

      {/* スクロール領域 (中央) */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto ios-scroll mobile-scroll"
        style={{
          paddingTop: topOffset + effectiveHeaderHeight + TOP_CONTENT_GAP,
          // 16(余白) + 64(footer h-16) + safe-area
          paddingBottom: "calc(80px + env(safe-area-inset-bottom, 0px))",
        }}
        onTouchStart={beginTouch}
        onTouchMove={moveTouch}
        onTouchEnd={endTouch}
        onTouchCancel={endTouch}
      >
        <div
          className="px-2 space-y-2 pb-32"
          style={{
            // リリース後戻るアニメーション
            transform:
              pullDistance > 0 && !refreshing
                ? `translateY(${pullDistance}px)`
                : "translateY(0px)",
            transition:
              (!pullingRef.current && pullDistance === 0) || refreshing
                ? "transform 200ms ease"
                : "none",
          }}
        >
          {sortedTasks.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              isMobile={true}
              isSelected={false}
              showSubtasksOnMobile={showSubtasksOnMobile}
              onSelectDetail={(parent, child) =>
                setDetailTask({
                  task: child || parent,
                  parent: parent,
                })
              }
              onToggleComplete={toggleTaskComplete}
              onUpdateStatus={updateTaskStatus}
              onToggleStar={toggleTaskStar}
              onToggleNotification={toggleTaskNotification}
              onToggleSubtask={() => {}}
              color={color}
              roomId={roomId}
              honbuId={honbuId}
              roomStatuses={roomStatuses}
              reorderSubtasks={reorderSubtasks}
              refreshTodos={refreshTodos}
              customColumns={customColumns}
              customColumnValues={customColumnValues}
              visibleCustomColumnIds={visibleCustomColumnIds}
            />
          ))}
          {/* タスクが無い場合の空状態表示 */}
          {sortedTasks.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400">
              <NightsStayTwoToneIcon
                style={{ fontSize: 48 }}
                className="mb-3"
              />
              <p className="text-sm leading-relaxed">
                このプロジェクトにはタスクがありません。
                <br />
                ⊕ボタンからタスクを作成してください。
              </p>
            </div>
          )}
          {/* カスタム項目表示設定（カスタム項目が設定されたプロジェクトのみ表示・スマホでは新規作成ボタンは非表示） */}
          {activeListId !== "0" && customColumns.length > 0 && (
            <div className="flex items-center justify-end px-1 pt-1">
              <button
                type="button"
                onClick={() => setShowCustomColumnDialog(true)}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-sm hover:bg-gray-100 focus:outline-none"
                style={{ color: PaletteColorHex[color] }}
                aria-label="表示するカスタム項目を設定"
                title="表示するカスタム項目を設定"
              >
                <ExtensionTwoToneIcon fontSize="small" />
                表示項目の変更
              </button>
            </div>
          )}
        </div>
      </div>

      {/* フィルタ / 詳細 / タグ / リスト追加 ダイアログ等 */}
      {showFilterDialog && (
        <FilterDialog
          onClose={() => setShowFilterDialog(false)}
          initialFilters={filters}
          onApplyFilters={(nf) => {
            setFilters(nf);
            setShowFilterDialog(false);
          }}
          availableTags={availableTags}
          roomMembers={roomMembers}
          color={color}
          roomStatuses={roomStatuses}
          roomId={roomId}
          activeListId={activeListId}
          honbuId={honbuId}
        />
      )}
      {detailTask && (
        <div className="fixed inset-0 bg-white z-50 animate-slide-in-bottom">
          <TaskDetailPanel
            task={detailTask.task}
            isSubtask={detailTask.task.id !== detailTask.parent.id}
            parentForContext={detailTask.parent}
            onUpdateTask={updateTask}
            onUpdateStatus={updateTaskStatus}
            onDeleteTask={deleteTask}
            roomMembers={roomMembers}
            availableTags={availableTags}
            onManageTags={() => setShowTagManagement(true)}
            color={color}
            addReminder={addReminder}
            deleteReminder={deleteReminder}
            onClose={() => setDetailTask(null)}
            displayMode="fullscreen"
            onAddSubtask={addSubtask}
            roomStatuses={roomStatuses}
            forceReload={forceReload}
            honbuId={honbuId}
            roomId={roomId}
            onOpenRecurrenceManagement={handleOpenRecurrenceManagement}
            onRecurrenceUpdateNeeded={handleRecurrenceUpdateNeededMobile}
            showSubtaskSelector={true}
            isMobile={true}
            listId={activeListId ? parseInt(activeListId, 10) : undefined}
            copyTargetLists={visibleLists.map((l) => ({
              id: Number(l.id),
              name: l.name,
            }))}
            onCopyToOtherList={handleCopyToOtherList}
            onTaskCopiedInList={handleTaskCopiedInList}
          />
        </div>
      )}
      {showTaskDialog && (
        <TaskDialog
          task={editingTask}
          initialStatus={initialStatusForNewTask}
          onClose={() => {
            setShowTaskDialog(false);
            setInitialStatusForNewTask(undefined);
          }}
          onSave={handleSaveTask}
          availableTags={availableTags}
          roomMembers={roomMembers}
          onManageTags={() => setShowTagManagement(true)}
          color={color}
          roomId={roomId}
          honbuId={honbuId}
          compactMobile={true}
          activeListId={activeListId ? parseInt(activeListId, 10) : null}
        />
      )}
      {showTagManagement && (
        <TagManagementDialog
          tags={availableTags}
          onClose={() => setShowTagManagement(false)}
          onAddTag={handleAddTag}
          onDeleteTag={handleDeleteTag}
          onUpdateTag={handleUpdateTag}
          color={color}
        />
      )}
      {editingProjectTemplateId !== null && (
        <ProjectTemplateEditorPanel
          projectTemplateId={editingProjectTemplateId}
          onClose={() => setEditingProjectTemplateId(null)}
          roomMembers={roomMembers}
          availableTags={availableTags}
          color={color}
          honbuId={honbuId}
          roomId={roomId}
          onUpdated={() => {}}
        />
      )}
      {showCreateListDialog && (
        <ListCreateDialog
          honbuId={honbuId}
          onClose={() => {
            setShowCreateListDialog(false);
            setShowCreatePrompt(false);
          }}
          roomId={roomId}
          onAddList={(listData) => {
            addTodoList(listData);
            handleCloseDialogs(true);
          }}
          showCreatePrompt={showCreatePrompt}
          color={color}
          onEditProjectTemplate={(id) => setEditingProjectTemplateId(id)}
        />
      )}
      {showEditListDialog && (
        <ListEditDialog
          key={JSON.stringify(todoLists.map((l) => `${l.id}-${l.name}`))}
          onClose={handleCloseDialogs}
          allLists={todoLists}
          onUpdateListStatus={updateListStatus}
          onDeleteList={deleteList}
          onUpdateListName={handleUpdateListNameAndRefreshUI}
          honbuId={honbuId}
          projectPins={projectPins}
          onTogglePin={toggleProjectPin}
          roomId={roomId}
          color={color}
        />
      )}
      {showGeneralSettingsDialog && (
        <GeneralSettingsDialog
          roomId={roomId}
          onClose={handleCloseGeneralSettings}
          forceReload={forceReload}
          tags={availableTags}
          onAddTag={handleAddTag}
          onDeleteTag={handleDeleteTag}
          onUpdateTag={handleUpdateTag}
          honbuId={honbuId}
          activeListId={activeListId ? parseInt(activeListId, 10) : null}
          todoLists={visibleLists.map((l) => ({ id: l.id, name: l.name }))}
          color={color}
        />
      )}
      {showCustomColumnDialog && (
        <TaskItemCustomColumnDialog
          customColumns={customColumns}
          initialVisibleIds={visibleCustomColumnIds}
          color={color}
          isDarkMode={isDarkMode}
          onClose={() => setShowCustomColumnDialog(false)}
          onSave={handleSaveVisibleCustomColumns}
        />
      )}
      {showReminderManagementMobile && (
        <ReminderManagementDialog
          onClose={() => {
            setShowReminderManagementMobile(false);
            refreshTodos();
          }}
          roomMembers={roomMembers}
          availableTags={availableTags}
          tasks={allTasksForDialogsMobile}
          color={color}
          honbuId={honbuId}
          roomId={roomId}
          onUpdateTask={updateTask}
          onUpdateStatus={updateTaskStatus}
          onDeleteTask={deleteTask}
          addReminder={addReminder}
          deleteReminder={deleteReminder}
          onManageTags={() => setShowTagManagement(true)}
          roomStatuses={roomStatuses}
          forceReload={async () => {
            await forceReload();
            await fetchAllTasksIncludingHiddenMobile();
          }}
          isMobile={true}
        />
      )}
      {showRecurrenceManagementMobile && (
        <RecurrenceManagementDialog
          onClose={() => {
            setShowRecurrenceManagementMobile(false);
            setRecurrenceInitialTaskIdMobile(undefined);
            setRecurrenceInitialRuleIdMobile(undefined);
            refreshTodos();
          }}
          roomMembers={roomMembers}
          availableTags={availableTags}
          color={color}
          honbuId={honbuId}
          roomId={roomId}
          currentListId={activeListId ? parseInt(activeListId, 10) : 0}
          onUpdateTask={updateTask}
          onDeleteTask={deleteTask}
          addReminder={addReminder}
          deleteReminder={deleteReminder}
          onManageTags={() => setShowTagManagement(true)}
          roomStatuses={roomStatuses}
          forceReload={async () => {
            await forceReload();
            await fetchAllTasksIncludingHiddenMobile();
          }}
          initialTaskId={recurrenceInitialTaskIdMobile}
          initialRuleId={recurrenceInitialRuleIdMobile}
          isMobile={true}
        />
      )}
      {/* プロジェクトメモダイアログ（モバイル用） */}
      {isMemoDialogOpen && activeListId && activeListId !== "0" && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setIsMemoDialogOpen(false)}
          />
          <div className="relative w-full max-w-lg bg-white rounded-t-2xl shadow-xl max-h-[85vh] flex flex-col animate-slide-up">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <span className="font-bold text-gray-700">プロジェクトメモ</span>
              <button
                onClick={() => setIsMemoDialogOpen(false)}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ProjectMemoPanel
                todoListId={parseInt(activeListId, 10)}
                honbuId={honbuId}
                roomId={roomId}
                roomMembers={roomMembers}
                color={color}
                onViewed={() => setHasMemoUnread(false)}
              />
            </div>
          </div>
        </div>
      )}
      {recurrenceUpdatePromptMobile && (
        <RecurrenceUpdatePrompt
          isOriginal={recurrenceUpdatePromptMobile.isOriginal}
          onConfirm={handleRecurrenceUpdateConfirmMobile}
          onDismiss={() => setRecurrenceUpdatePromptMobile(null)}
          isLoading={recurrenceUpdateLoadingMobile}
        />
      )}
      {/* ヘルプドロワー（モバイルは常にオーバーレイ） */}
      <HelpDrawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        paletteColor={color}
        isDark={isDarkMode}
        variant="overlay"
        honbuId={honbuId}
        roomId={roomId}
        isMobile={true}
        onTaskMutated={refreshTodos}
      />
    </div>
  );
}

// ===== アプリ全体のエントリーポイント =====
function NoAuthScreen() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-6">
      <div className="bg-white shadow rounded-lg p-6 max-w-md w-full space-y-4">
        <h1 className="text-lg font-semibold text-gray-800">ChatisTODO</h1>
        <p className="text-sm text-gray-600">
          チャットアプリからこのアプリを開いてください。
          <br />
          一度チャットアプリ経由でアクセスすると、次回からデスクトップショートカットで直接開けます。
        </p>
      </div>
    </div>
  );
}

export default function App() {
  // ================= isTest フラグ =================
  // true = 認証あり / false = 認証スキップ
  const enableAuth = true;
  // ================================================

  // URLパラメータからhonbuId/roomId取得
  const { isMobile } = useWindowSize();
  const { honbuId, roomId, color, isDarkMode, loaded, isShortcut } =
    useUrlParams();
  const [isAppLoading, setAppLoading] = useState(true);

  // 認証ステート（isTest=0 の場合は即 false / null）
  const [isAuthChecking, setIsAuthChecking] = useState(enableAuth);
  const [authError, setAuthError] = useState<string | null>(null);

  // トークン失効で /goto 経由の再入場もできなかった場合の通知先を登録する
  useEffect(() => {
    setAuthFailureHandler(setAuthError);
    return () => setAuthFailureHandler(null);
  }, []);

  // 認証（enableAuth=true の時だけ実行）
  useEffect(() => {
    if (!enableAuth) return;
    if (honbuId === null || roomId === null) return;

    // sessionStorage に有効なトークンがある＝このタブは認証済み。
    // Chatis の temp token はワンショットのため、リロードで /api/check を
    // 呼び直すと必ず 401 になる。トークンが無いときだけ /api/check を叩く。
    if (restoreApiAuth(honbuId, roomId)) {
      clearReentering();
      setIsAuthChecking(false);
      return;
    }

    const paramHon = honbuId.toString();
    const paramRoom = roomId.toString();
    (async () => {
      setIsAuthChecking(true);
      setAuthError(null);
      let redirecting = false;
      try {
        const res = await fetch(
          `/api/check?honbuid=${paramHon}&roomid=${paramRoom}`,
        );
        if (!res.ok) {
          if (res.status === 401) {
            // 既存のデスクトップショートカットは Chatis を経由しないため
            // temp token を持たない。/goto へ 1 回だけバウンスして入り直す。
            if (isShortcut && reenterViaChatis()) {
              redirecting = true;
              return;
            }
            setAuthError(
              "認証に失敗しました。Chatis の TODO ボタンから開き直してください。",
            );
          } else {
            setAuthError(`サーバーエラー (${res.status}) が発生しました。`);
          }
          return;
        }
        const json = await res.json();
        if (typeof json?.token !== "string" || !json.token) {
          setAuthError("認証応答にトークンが含まれていません。");
          return;
        }
        // 以降の API 呼び出しで使うアクセストークンを保持する（sessionStorage）
        setApiAuth(json.token, honbuId, roomId);
        clearReentering();
        // 表示設定を保存（ショートカット起動時の初期値として使う）
        saveStoredAuth({ honbuId, roomId, color, isDarkMode });
      } catch {
        setAuthError("認証サーバーとの通信に失敗しました。");
      } finally {
        // リダイレクト中は画面を描画させない（トークンなしで API を叩かせないため）
        if (!redirecting) setIsAuthChecking(false);
      }
    })();
  }, [enableAuth, honbuId, roomId, isShortcut, color, isDarkMode]);
  // 認証チェック表示は isTest=1 の時のみ評価
  if (enableAuth) {
    if (!loaded || isAuthChecking) {
      return <LoadingOverlay color={color} />;
    }
    if (honbuId === null || roomId === null) {
      return <NoAuthScreen />;
    }
    if (authError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 p-6">
          <div className="bg-white shadow rounded-lg p-6 max-w-md w-full space-y-4">
            <h1 className="text-lg font-semibold text-red-600">認証エラー</h1>
            <p className="text-sm text-gray-700 whitespace-pre-line">
              {authError}
            </p>
          </div>
        </div>
      );
    }
  }

  // 認証オフ時のフォールバック ID
  const effectiveHonbuId = honbuId ?? null;
  const effectiveRoomId = roomId ?? null;
  if (!loaded) {
    return <LoadingOverlay color={color} />;
  }
  if (effectiveHonbuId === null || effectiveRoomId === null) {
    return <NoAuthScreen />;
  }

  const shortcutOffset = isShortcut ? 48 : 0;

  // PC/モバイルでビューを切り替えて描画
  return (
    <>
      {isAppLoading && <LoadingOverlay color={color} />}
      <div
        style={{
          display: isAppLoading ? "none" : "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        {isShortcut && (
          <ShortcutHeader
            honbuId={effectiveHonbuId}
            roomId={effectiveRoomId}
            color={color}
          />
        )}
        <div style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
          {isMobile ? (
            <MobileView
              honbuId={effectiveHonbuId}
              roomId={effectiveRoomId}
              setAppLoading={setAppLoading}
              topOffset={shortcutOffset}
            />
          ) : (
            <DesktopView
              honbuId={effectiveHonbuId}
              roomId={effectiveRoomId}
              setAppLoading={setAppLoading}
            />
          )}
        </div>
      </div>
    </>
  );
}
